import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Paper, Button, Chip,
  TextField, MenuItem, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Divider, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Switch, FormControlLabel, Badge, Menu, Slider,
} from '@mui/material';

const API_BASE = '/api/dashboard/apps/playlanguage';

// L.1.6 — input + dropdown colors for dark theme
const INPUT_SX = {
  '& .MuiInputBase-input':        { color: '#fff' },
  '& .MuiInputBase-input::placeholder': { color: '#888' },
  '& .MuiInputLabel-root':        { color: '#bbb' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--ac1, #7c4dff)' },
  '& .MuiOutlinedInput-notchedOutline':            { borderColor: '#555' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--ac1, #7c4dff)' },
  '& .MuiSelect-icon':            { color: '#aaa' },
};
// Select dropdown popup (L.1.6 fix — чорний на чорному)
const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      bgcolor: '#1a0a2e',
      color: '#fff',
      border: '1px solid #444',
      '& .MuiMenuItem-root': { color: '#fff' },
      '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.20)' },
      '& .MuiMenuItem-root.Mui-selected': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.35)' },
    },
  },
};

async function jsonFetch(url, opts = {}) {
  // 💎 PlayLanguage security fix: send the dashboard Bearer token on every call.
  // PL admin endpoints now require verify_token (were unauthenticated); cookie alone
  // is not enough — the API auth is OAuth2 Bearer (localStorage 'dashboard_token').
  const r = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}`,
    },
    credentials: 'include', ...opts,
  });
  if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
  return r.json();
}
const fmtEur = (n) => `€${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s).toLocaleString() : '—';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// 💎 Порядок вкладок — перетягуванням (drag&drop), зберігається у localStorage (per-browser).
// id = оригінальний index вкладки (TabPanel-и лишаються index-keyed і не залежать від порядку).
const PL_TAB_COUNT = 14;
const PL_TAB_ORDER_KEY = 'pl_admin_tab_order';
function loadPlTabOrder() {
  const def = Array.from({ length: PL_TAB_COUNT }, (_, i) => i);
  try {
    const raw = JSON.parse(localStorage.getItem(PL_TAB_ORDER_KEY));
    if (Array.isArray(raw)) {
      const valid = raw.filter(x => Number.isInteger(x) && x >= 0 && x < PL_TAB_COUNT);
      const seen = new Set(valid);
      def.forEach(i => { if (!seen.has(i)) valid.push(i); }); // нові вкладки → у кінець
      if (valid.length === PL_TAB_COUNT) return valid;
    }
  } catch (_e) { /* зіпсований/порожній localStorage → дефолт */ }
  return def;
}

// =============================================================================
// ConfigTab L.1.6: simplified promo (1 UA) + requisites (1 UA, TODO pull from Media)
// =============================================================================

// [PL-MUSIC] 🎵 Фонова музика PlayLanguage — дзеркало D-301 (Хаб), список вантажиться на mount
function PlMusicSection() {
  const [tracks, setTracks] = React.useState([]);
  const [up, setUp] = React.useState(false);
  const inp = React.useRef(null);
  const H = () => ({ Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` });
  const load = () => fetch('/api/pl-music', { headers: H() }).then(r => r.json()).then(d => setTracks(Array.isArray(d) ? d : [])).catch(() => {});
  React.useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = '';
    if (!f) return; setUp(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await fetch('/api/pl-music/upload', { method: 'POST', body: fd, headers: H() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); alert('Помилка: ' + (d.detail || r.status)); }
      load();
    } catch (er) { alert('Помилка мережі'); }
    setUp(false);
  };
  const tgl = async (id) => { try { await fetch('/api/pl-music/' + id + '/toggle', { method: 'POST', headers: H() }); load(); } catch (e) {} };
  const del = async (id) => { if (!window.confirm('Видалити трек?')) return; try { await fetch('/api/pl-music/' + id, { method: 'DELETE', headers: H() }); load(); } catch (e) {} };
  return (
    <Paper sx={{ p: 2, bgcolor: '#1a1a2e', border: '1px solid rgba(var(--ac1rgb, 124, 77, 255),.35)', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ color: 'var(--ac2, #a78bfa)', fontWeight: 'bold' }}>🎵 Фонова музика PlayLanguage</Typography>
        <Button size="small" variant="outlined" disabled={up} onClick={() => inp.current && inp.current.click()}
          sx={{ color: 'var(--ac2, #a78bfa)', borderColor: 'var(--ac2, #a78bfa)', fontSize: 12 }}>
          {up ? '⏳ Завантаження…' : '⬆ Додати трек'}
        </Button>
        <input ref={inp} type="file" accept="audio/*,.mp3,.ogg,.m4a,.aac,.wav" style={{ display: 'none' }} onChange={upload} />
        <Typography sx={{ color: '#667', fontSize: 11 }}>рандомний трек у PL-додатку · окремо від Хаба · макс 12MB</Typography>
      </Box>
      {tracks.length === 0 && <Typography sx={{ color: '#777', fontSize: 12 }}>Ще немає треків. Додай — гратимуть рандомно при вході у PL.</Typography>}
      {tracks.map(tr => (
        <Box key={tr.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #26263e', py: 0.7 }}>
          <span style={{ fontSize: 16 }}>{tr.enabled ? '🎶' : '🔇'}</span>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: '#fff', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tr.original_name || tr.filename}</Typography>
            <audio src={tr.url} controls preload="none" style={{ height: 28, width: '100%', maxWidth: 260 }} />
          </Box>
          <Button size="small" onClick={() => tgl(tr.id)} sx={{ color: tr.enabled ? 'var(--ac2, #a78bfa)' : '#888', fontSize: 11, minWidth: 0 }}>{tr.enabled ? 'вкл' : 'викл'}</Button>
          <Button size="small" onClick={() => del(tr.id)} sx={{ color: '#f87171', fontSize: 11, minWidth: 0 }}>🗑</Button>
        </Box>
      ))}
    </Paper>
  );
}


// [PL-DRAG] Перетягувані секції Config (як картки System page): ручка ⠿, порядок у localStorage
function DragSections({ storageKey, children }) {
  const items = React.Children.toArray(children);
  const norm = (k) => String(k || '').replace(/^\.\$/, '');
  const keys = items.map(c => norm(c.key));
  const [order, setOrder] = React.useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (Array.isArray(s)) {
        const valid = s.filter(k => keys.includes(k));
        return [...valid, ...keys.filter(k => !valid.includes(k))];
      }
    } catch (e) {}
    return keys;
  });
  const ordered = order.map(k => items.find(c => norm(c.key) === k)).filter(Boolean);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const o = [...order]; const [m] = o.splice(i, 1); o.splice(j, 0, m);
    setOrder(o); localStorage.setItem(storageKey, JSON.stringify(o));
  };
  const arrowSx = { minWidth: 0, width: 26, height: 22, p: 0, fontSize: 13, lineHeight: 1,
    color: '#889', border: '1px solid #33334d', borderRadius: 1,
    '&:hover': { color: 'var(--ac2, #a78bfa)', borderColor: 'var(--ac2, #a78bfa)' }, '&.Mui-disabled': { color: '#333', borderColor: '#222' } };
  return ordered.map((child, i) => (
    <Box key={'w_' + norm(child.key)} sx={{ position: 'relative', mb: 2 }}>
      <Box sx={{ position: 'absolute', top: 4, right: 6, zIndex: 5, display: 'flex', gap: 0.5 }}>
        <Button sx={arrowSx} disabled={i === 0} onClick={() => move(i, -1)} title="Вище">▲</Button>
        <Button sx={arrowSx} disabled={i === ordered.length - 1} onClick={() => move(i, 1)} title="Нижче">▼</Button>
      </Box>
      {child}
    </Box>
  ));
}

function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [requisitesUk, setRequisitesUk] = useState('');
  const [promoUk, setPromoUk] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sounds, setSounds] = useState({ correct: null, wrong: null, confetti: null });
  const [soundUploading, setSoundUploading] = useState({ correct: false, wrong: false, confetti: false });
  const [soundMsg, setSoundMsg] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg] = useState(null);
  // D-258: фото над текстом вітання бота (окреме від лого)
  const [welImg, setWelImg] = useState(null);
  const [welImgUploading, setWelImgUploading] = useState(false);
  const [welImgMsg, setWelImgMsg] = useState(null);
  // 💎 D-237: mono-Банка (оплата карткою) — токен зберігається окремо в api_keys
  const [monoToken, setMonoToken] = useState('');
  const [monoStatus, setMonoStatus] = useState(null);
  const [monoMsg, setMonoMsg] = useState(null);

  const monoSaveToken = async () => {
    setMonoMsg(null);
    try {
      await jsonFetch(`${API_BASE}/mono-token`, { method: 'POST', body: JSON.stringify({ token: monoToken.trim() }) });
      setMonoToken('');
      setMonoMsg({ type: 'success', text: 'Токен збережено (в api_keys, назад не показується)' });
    } catch (e) { setMonoMsg({ type: 'error', text: e.message }); }
  };
  const monoCheckStatus = async () => {
    setMonoMsg(null);
    try { setMonoStatus(await jsonFetch(`${API_BASE}/mono-status`)); }
    catch (e) { setMonoMsg({ type: 'error', text: e.message }); }
  };
  const monoSetWebhook = async () => {
    setMonoMsg(null);
    try {
      const r = await jsonFetch(`${API_BASE}/mono-webhook-set`, { method: 'POST', body: '{}' });
      setMonoMsg(r.ok ? { type: 'success', text: '⚡ Webhook зареєстровано — підтвердження миттєві' }
                      : { type: 'error', text: `mono відповів ${r.status}` });
    } catch (e) { setMonoMsg({ type: 'error', text: e.message }); }
  };

  const loadSounds = useCallback(() => {
    jsonFetch(`${API_BASE}/config`).then(r => {
      setSounds({
        correct:   (r.config.sound_correct   || null),
        wrong:     (r.config.sound_wrong     || null),
        confetti:  (r.config.sound_confetti  || null),
      });
      setLogo(r.config.logo_url || null);
      setWelImg(r.config.welcome_image_url || null);
    }).catch(() => {});
  }, []);
  useEffect(loadSounds, [loadSounds]);

  const uploadLogo = async (file) => {
    setLogoUploading(true); setLogoMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/logo`, { method: 'POST', body: fd, credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` } });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Upload failed'); }
      const data = await res.json();
      setLogo(data.url);
      setLogoMsg({ type: 'success', text: `Завантажено: ${file.name}` });
    } catch (e) { setLogoMsg({ type: 'error', text: e.message }); }
    finally { setLogoUploading(false); }
  };

  const deleteLogo = async () => {
    setLogoMsg(null);
    try {
      await jsonFetch(`${API_BASE}/logo`, { method: 'DELETE' });
      setLogo(null);
      setLogoMsg({ type: 'success', text: 'Логотип видалено — використовується 💎' });
    } catch (e) { setLogoMsg({ type: 'error', text: e.message }); }
  };

  // D-258: фото над вітанням бота
  const uploadWelImg = async (file) => {
    setWelImgUploading(true); setWelImgMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/welcome-image`, { method: 'POST', body: fd, credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` } });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Upload failed'); }
      const data = await res.json();
      setWelImg(data.url);
      setWelImgMsg({ type: 'success', text: `Завантажено: ${file.name}` });
    } catch (e) { setWelImgMsg({ type: 'error', text: e.message }); }
    finally { setWelImgUploading(false); }
  };

  const deleteWelImg = async () => {
    setWelImgMsg(null);
    try {
      await jsonFetch(`${API_BASE}/welcome-image`, { method: 'DELETE' });
      setWelImg(null);
      setWelImgMsg({ type: 'success', text: 'Фото видалено — бот покаже логотип' });
    } catch (e) { setWelImgMsg({ type: 'error', text: e.message }); }
  };

  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/config`).then(r => {
      setCfg(r.config);
      setRequisitesUk((r.config.donate_requisites_i18n || {}).uk || '');
      setPromoUk((r.config.promo_text_i18n || {}).uk || '');
    });
  }, []);
  useEffect(load, [load]);

  const updateField = (key, value) =>
    setCfg(prev => ({ ...prev, [key]: value }));

  const uploadSound = async (type, file) => {
    setSoundUploading(p => ({ ...p, [type]: true }));
    setSoundMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/sounds/upload?sound_type=${type}`, {
        method: 'POST', body: fd,
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Upload failed'); }
      const data = await res.json();
      setSounds(p => ({ ...p, [type]: data.url }));
      setSoundMsg({ type: 'success', text: `Завантажено: ${file.name}` });
    } catch (e) {
      setSoundMsg({ type: 'error', text: e.message });
    } finally { setSoundUploading(p => ({ ...p, [type]: false })); }
  };

  const deleteSound = async (type) => {
    setSoundMsg(null);
    try {
      await jsonFetch(`${API_BASE}/sounds?sound_type=${type}`, { method: 'DELETE' });
      setSounds(p => ({ ...p, [type]: null }));
      setSoundMsg({ type: 'success', text: 'Звук видалено — використовуються тони' });
    } catch (e) { setSoundMsg({ type: 'error', text: e.message }); }
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload = {
        ...cfg,
        // зберігаємо тільки UA — backend buduє fallback chain (uk → en) для усіх langs
        donate_requisites_i18n: { ...(cfg.donate_requisites_i18n || {}), uk: requisitesUk },
        promo_text_i18n:        { ...(cfg.promo_text_i18n        || {}), uk: promoUk },
      };
      await jsonFetch(`${API_BASE}/config`, {
        method: 'PUT', body: JSON.stringify({ config: payload }),
      });
      setMsg({ type: 'success', text: 'Збережено' });
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally { setSaving(false); }
  };

  if (!cfg) return <CircularProgress />;

  return (
    <Stack spacing={2} sx={INPUT_SX}>
      <Typography variant="h6" sx={{ color: '#fff' }}>⚙️ Конфігурація</Typography>

      <DragSections storageKey="pl_config_order">
      {/* 💎 Q-PL-012: преміум-аналітика €-гейт + ігрові/фото пороги (зберігаються разом із Config) */}
      <Box key="premium" sx={{ p: 1.5, borderRadius: 2, border: '1px solid var(--ac2, #a78bfa)', bgcolor: 'rgba(124,58,237,0.08)' }}>
        <Typography sx={{ color: '#c4b5fd', fontWeight: 600, mb: 1 }}>💎 Преміум-аналітика та гра (пороги)</Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField label="Аналітика: оплата від, €" type="number" size="small" sx={{ width: 210 }}
            value={cfg.analytics_min_paid_eur ?? 5} onChange={e => updateField('analytics_min_paid_eur', Number(e.target.value))} />
          <TextField label="Гра: відповідей за нагороду" type="number" size="small" sx={{ width: 210 }}
            value={cfg.game_reward_threshold ?? 30} onChange={e => updateField('game_reward_threshold', Number(e.target.value))} />
          <TextField label="Гра: днів преміуму" type="number" size="small" sx={{ width: 210 }}
            value={cfg.game_reward_days ?? 1} onChange={e => updateField('game_reward_days', Number(e.target.value))} />
          <TextField label="Гра: кулдаун free, днів" type="number" size="small" sx={{ width: 210 }}
            value={cfg.game_free_cooldown_days ?? 7} onChange={e => updateField('game_free_cooldown_days', Number(e.target.value))} />
          <TextField label="Гра: мін. диспутерів" type="number" size="small" sx={{ width: 210 }}
            value={cfg.game_min_disputers ?? 5} onChange={e => updateField('game_min_disputers', Number(e.target.value))} />
          <TextField label="Фото-лок: голосів" type="number" size="small" sx={{ width: 210 }}
            value={cfg.photo_lock_votes ?? 7} onChange={e => updateField('photo_lock_votes', Number(e.target.value))} />
        </Stack>
      </Box>

      {/* 💎 D-263: повзунок — частка «легких» слів для не-початківців у Матчі */}
      <Box key="sec_violet" sx={{ p: 1.5, borderRadius: 2, border: '1px solid var(--ac1, #7c4dff)', bgcolor: 'rgba(124,58,237,0.08)' }}>
        <Typography sx={{ color: '#c4b5fd', fontWeight: 600, mb: 0.25 }}>
          {'🎚 Легкі слова для досвідчених: '}<b style={{ color: '#fff' }}>{(cfg.nonbeginner_lower_mix_pct ?? 15) + '%'}</b>
        </Typography>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
          Як часто не-початківцю (рівень ≥2) у Матчі підкидається легше слово з нижчого рівня — для легких перемог і набивання Бази знань (анти-демотивація). 0 = вимкнено.
        </Typography>
        <Slider min={0} max={40} step={1} valueLabelDisplay="auto"
          value={cfg.nonbeginner_lower_mix_pct ?? 15}
          onChange={(_e, v) => updateField('nonbeginner_lower_mix_pct', v)}
          sx={{ color: 'var(--ac1, #7c4dff)', maxWidth: 420 }} />
      </Box>

      {/* 💎 01.09.2026 (OWNER): частка не-впізнаних слів у Матчі */}
      <Box key="sec_retry" sx={{ p: 1.5, borderRadius: 2, border: '1px solid #f59e0b', bgcolor: 'rgba(245,158,11,0.08)' }}>
        <Typography sx={{ color: '#fcd34d', fontWeight: 600, mb: 0.25 }}>
          {'🔁 Повтор не-впізнаних слів у Матчі: '}<b style={{ color: '#fff' }}>{(cfg.match_retry_mix_pct ?? 30) + '%'}</b>
        </Typography>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
          Слово, яке юзер уже бачив і НЕ впізнав, повертається на повтор. Повзунок каже, як часто Матч дає саме таке слово замість НОВОГО. Решту разів показується нове, а не-впізнане чекає своєї черги (не втрачається: якщо нових слів не лишилось, воно віддається однаково). 0 = завжди спершу нове.
        </Typography>
        <Slider min={0} max={100} step={5} valueLabelDisplay="auto"
          value={cfg.match_retry_mix_pct ?? 30}
          onChange={(_e, v) => updateField('match_retry_mix_pct', v)}
          sx={{ color: '#f59e0b', maxWidth: 420 }} />
      </Box>

      {/* 💎 D-237: оплата карткою — mono-Банка (авто) + Revolut (вручну) */}
      <Box key="sec_teal" sx={{ p: 1.5, borderRadius: 2, border: '1px solid #5eead4', bgcolor: 'rgba(94,234,212,0.06)' }}>
        <Typography sx={{ color: '#5eead4', fontWeight: 600, mb: 1 }}>💳 Оплата карткою (mono-Банка + Revolut)</Typography>
        <FormControlLabel sx={{ color: '#fff', mb: 1 }}
          control={<Switch checked={!!cfg.mono_enabled && String(cfg.mono_enabled) !== 'false'}
            onChange={e => updateField('mono_enabled', e.target.checked)} />}
          label="Увімкнути mono-оплату в апці" />
        <Stack spacing={1.5}>
          <TextField label="Лінк Банки (EUR) — send.monobank.ua/…" size="small" fullWidth
            value={cfg.mono_jar_url_eur || ''} onChange={e => updateField('mono_jar_url_eur', e.target.value)} />
          <TextField label="Лінк Банки (UAH) — send.monobank.ua/…" size="small" fullWidth
            value={cfg.mono_jar_url_uah || ''} onChange={e => updateField('mono_jar_url_uah', e.target.value)} />
          <TextField label="Revolut-лінк (revolut.me/…)" size="small" fullWidth
            value={cfg.revolut_link || ''} onChange={e => updateField('revolut_link', e.target.value)} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField label="monobank API-токен (api.monobank.ua)" size="small" type="password"
              sx={{ flex: 1, minWidth: 240 }} value={monoToken} onChange={e => setMonoToken(e.target.value)} />
            <Button variant="outlined" disabled={monoToken.trim().length < 20} onClick={monoSaveToken}>
              🔑 Зберегти токен
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={monoCheckStatus}>🔄 Перевірити зв'язок</Button>
            <Button variant="outlined" onClick={monoSetWebhook}>⚡ Увімкнути webhook</Button>
          </Stack>
          {monoStatus && (
            <Typography variant="caption" sx={{ color: monoStatus.has_token ? '#5eead4' : '#fbbf24' }}>
              {monoStatus.has_token
                ? (monoStatus.error
                    ? `Токен є, але помилка: ${monoStatus.error}`
                    : `Токен ✅. Банки: ${(monoStatus.jars || []).map(j => `«${j.title}» (${j.currency}, …${j.send_tail.slice(-6)})`).join(', ') || 'не знайдено'}`)
                : 'Токен не збережено'}
            </Typography>
          )}
          {monoMsg && <Alert severity={monoMsg.type}>{monoMsg.text}</Alert>}
          <Typography variant="caption" sx={{ color: '#888' }}>
            Авто-підтвердження: юзер платить суму з унікальними копійками → платіж знаходиться у виписці Банки → доступ видається сам. Revolut — оплата за лінком + чек вручну. Не забудь «Зберегти» внизу після зміни лінків/перемикача.
          </Typography>
        </Stack>
      </Box>

      {/* D-297: Stripe (MediaSystemGroup) — ДОДАТКОВИЙ спосіб, власний незалежний тумблер
          stripe_enabled (app_playlanguage_config, НЕ referral_settings) — D-283, не коуплено з mono. */}
      <Box key="sec_stripe" sx={{ p: 1.5, borderRadius: 2, border: '1px solid #ffd166', bgcolor: 'rgba(255,209,102,0.06)' }}>
        <Typography sx={{ color: '#ffd166', fontWeight: 600, mb: 1 }}>🅢 Оплата карткою (Stripe, спільний акаунт)</Typography>
        <FormControlLabel sx={{ color: '#fff', mb: 1 }}
          control={<Switch checked={!!cfg.stripe_enabled && String(cfg.stripe_enabled) !== 'false'}
            onChange={e => updateField('stripe_enabled', e.target.checked)} />}
          label="Увімкнути Stripe-оплату донату в апці" />
        <Typography variant="caption" sx={{ color: '#888' }}>
          Той самий спільний Stripe-акаунт MediaSystemGroup, що й у NUMERIQ/Хіромантії/ZODIAQ (через міст AI Assistant).
          Юзер вводить суму → редірект на Stripe → доступ (+дні донату) видається сам вебхуком, без ручного «я оплатив».
          Незалежно від mono/Revolut/крипто — можна тримати увімкненими кілька способів одночасно.
        </Typography>
      </Box>

      <PlMusicSection key="music" />

      {/* 💎 D-283: USDT / крипто-оплата */}
      <Box key="usdt" sx={{ p: 1.5, borderRadius: 2, border: '1px solid #fbbf24', bgcolor: 'rgba(251,191,36,0.06)' }}>
        <Typography sx={{ color: '#fbbf24', fontWeight: 600, mb: 1 }}>💰 USDT (TRC-20) / Крипто-оплата</Typography>
        <Stack spacing={1.5}>
          <FormControlLabel sx={{ color: '#fff' }}
            control={<Switch checked={!!(cfg.pay_show_crypto && String(cfg.pay_show_crypto) !== 'false')}
              onChange={e => updateField('pay_show_crypto', e.target.checked)} />}
            label="Показувати крипто-блок у міні-аппі" />
          <TextField label="USDT-адреса (TRC-20)" size="small" fullWidth
            placeholder="TYour...Address"
            value={cfg.pay_crypto_address || ''}
            onChange={e => updateField('pay_crypto_address', e.target.value)}
            helperText="Увімкни перемикач + встав адресу + натисни «Зберегти» — блок з'явиться в апці."
            FormHelperTextProps={{ sx: { color: '#888' } }} />
          <TextField label="Назва методу (за замовड़. USDT (TRC20))" size="small" fullWidth
            placeholder="USDT (TRC20)"
            value={cfg.pay_crypto_label || ''}
            onChange={e => updateField('pay_crypto_label', e.target.value)} />
        </Stack>
      </Box>

            {/* 💎 PL Phase E: manual maintenance (tech-works) toggle */}
      <Box key="maint" sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: cfg.maintenance_mode ? '#fbbf24' : '#444', bgcolor: cfg.maintenance_mode ? 'rgba(251,191,36,0.10)' : 'transparent' }}>
        <FormControlLabel sx={{ color: '#fff' }}
          control={
            <Switch checked={!!cfg.maintenance_mode}
              onChange={e => updateField('maintenance_mode', e.target.checked)} />
          }
          label={cfg.maintenance_mode ? '🛠️ Технічні роботи УВІМКНЕНО — користувачі бачать екран «технічні роботи»' : '🛠️ Режим технічних робіт (вимкнено)'} />
        <Typography variant="caption" sx={{ display: 'block', color: '#888', mt: 0.5 }}>
          Коли увімкнено — у міні-аппі показується екран «Йдуть технічні роботи». Щойно вимкнеш і збережеш — усім, хто натиснув «Сповістити мене», надійде сповіщення «PlayLanguage знову працює».
        </Typography>
      </Box>

      {/* 💎 PL Урок-1 (онбординг) — kill-switch. Дрімно поки OFF */}
      <Box key="onboard" sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: cfg.onboarding_lesson_enabled ? '#34d399' : '#444', bgcolor: cfg.onboarding_lesson_enabled ? 'rgba(52,211,153,0.10)' : 'transparent' }}>
        <FormControlLabel sx={{ color: '#fff' }}
          control={
            <Switch checked={!!cfg.onboarding_lesson_enabled}
              onChange={e => updateField('onboarding_lesson_enabled', e.target.checked)} />
          }
          label={cfg.onboarding_lesson_enabled ? '🎓 Урок-1 (онбординг) УВІМКНЕНО — нові «Початківці» бачать гайд' : '🎓 Урок-1 (онбординг для новачків) — вимкнено'} />
        <Typography variant="caption" sx={{ display: 'block', color: '#888', mt: 0.5 }}>
          Веде нового користувача рівня «Початківець» через 5 кроків (Матч → Тест → Квіз → Практика → Аналітика) на його першому слові. Має кнопку «Пропустити». Вмикає лише для НОВИХ новачків — на наявних користувачів не впливає. Це аварійний вимикач: вимкнеш — гайд миттєво зникає.
        </Typography>
      </Box>

      {/* 💎 Q-PL-023: юзер прибирає непотрібні слова. Дрімно поки OFF */}
      <Box key="wordhide" sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: cfg.word_hide_enabled ? '#34d399' : '#444', bgcolor: cfg.word_hide_enabled ? 'rgba(52,211,153,0.10)' : 'transparent' }}>
        <FormControlLabel sx={{ color: '#fff' }}
          control={
            <Switch checked={!!cfg.word_hide_enabled}
              onChange={e => updateField('word_hide_enabled', e.target.checked)} />
          }
          label={cfg.word_hide_enabled ? '🚫 Приховування слів УВІМКНЕНО — юзери бачать кнопку «перекреслене око»' : '🚫 Приховування слів юзером — вимкнено'} />
        <Typography variant="caption" sx={{ display: 'block', color: '#888', mt: 0.5 }}>
          Кнопка «перекреслене око» на картках Матчу й Квіза та в тап-перекладі Практики: користувач прибирає слово, яке йому не знадобиться. Слово не видаляється — воно переїжджає в його особистий архів (Хаб → «Приховані слова»), звідки повертається РАЗОМ із прогресом. Слова з Бази знань прибрати не можна — з них будується Практика (сервер відхиляє це сам). У Практиці поряд є кнопка «Хочу вивчити»: нею юзер бере в навчання слово, яке лише підмішалось у речення. Це аварійний вимикач: вимкнеш — кнопки миттєво зникають, уже приховані слова лишаються прихованими. Не забудь «💾 Зберегти» внизу — сам перемикач нічого не записує.
        </Typography>
      </Box>

      {/* 💎 D-285: тех-підтримка PL — куди пересилати звернення (гілка «Тех.підтримка PL») */}
      <Box key="sec_blue" sx={{ p: 1.5, borderRadius: 2, border: '1px solid #38bdf8', bgcolor: 'rgba(56,189,248,0.06)' }}>
        <Typography sx={{ color: '#38bdf8', fontWeight: 600, mb: 1 }}>🛠 Тех-підтримка PL</Typography>
        <Stack spacing={1.5}>
          <TextField label="Chat ID групи (напр. -1001234567890)" size="small" fullWidth
            value={cfg.pl_support_chat_id || ''} onChange={e => updateField('pl_support_chat_id', e.target.value)} />
          <TextField label="Thread ID гілки «Тех.підтримка PL»" size="small" fullWidth
            value={cfg.pl_support_thread_id || ''} onChange={e => updateField('pl_support_thread_id', e.target.value)} />
          <Typography variant="caption" sx={{ color: '#888' }}>
            Звернення з міні-аппа пересилаються сюди (через адмін-бота, з авто-перекладом на українську). Якщо порожньо — падають у загальну гілку алертів. Відповідати — у вкладці «🛠 Підтримка». Не забудь «Зберегти» внизу.
          </Typography>
        </Stack>
      </Box>

      <Box key="sounds" sx={{ p: 1.5, borderRadius: 2, border: '1px solid var(--ac1, #7c4dff)', bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.05)' }}>
      <Typography variant="subtitle2" sx={{ color: 'var(--ac1, #7c4dff)' }}>🔊 Звуки відповідей</Typography>
      <Typography variant="caption" sx={{ color: '#888' }}>
        mp3 / wav / ogg · макс 5 МБ · якщо не завантажено — використовуються генеровані тони
      </Typography>
      {[['correct','✅ Правильна відповідь'],['wrong','❌ Неправильна відповідь'],['confetti','🎉 Слово вивчено (mastery 5)']].map(([type, label]) => (
        <Paper key={type} sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography sx={{ color: '#fff', minWidth: 200, fontSize: 14 }}>{label}</Typography>
            {sounds[type] ? (
              <>
                <Chip label={sounds[type].split('/').pop()} size="small" sx={{ bgcolor: '#1a3a1a', color: '#4ade80' }} />
                <Button size="small" variant="outlined" sx={{ color: '#60a5fa', borderColor: '#60a5fa' }}
                  onClick={() => { try { new Audio(sounds[type]).play(); } catch(e){} }}>
                  ▶ Preview
                </Button>
                <Button size="small" variant="outlined" color="error"
                  onClick={() => deleteSound(type)}>🗑 Видалити</Button>
              </>
            ) : (
              <Chip label="тони (за замовч.)" size="small" sx={{ bgcolor: '#2a2a2a', color: '#888' }} />
            )}
            <Button size="small" component="label" variant="contained"
              disabled={soundUploading[type]}
              sx={{ bgcolor: '#7c3aed' }}>
              {soundUploading[type] ? '⏳' : '📁 Завантажити'}
              <input type="file" hidden accept=".mp3,.wav,.ogg,.m4a"
                onChange={e => { if (e.target.files[0]) uploadSound(type, e.target.files[0]); e.target.value=''; }} />
            </Button>
          </Stack>
        </Paper>
      ))}
      </Box>

      </DragSections>

      <Stack direction="row" spacing={2} flexWrap="wrap">
        <TextField label="Trial днів" type="number" size="small"
          value={cfg.trial_days || 5}
          onChange={e => updateField('trial_days', parseInt(e.target.value) || 0)} />
        <TextField label="Сума донату (€)" type="number" size="small"
          value={cfg.donate_amount_eur || 10}
          onChange={e => updateField('donate_amount_eur', parseFloat(e.target.value) || 0)} />
        <TextField label="Доступ днів (після donate)" type="number" size="small"
          value={cfg.access_days || 365}
          onChange={e => updateField('access_days', parseInt(e.target.value) || 365)}
          helperText="365 = 1 рік"
          FormHelperTextProps={{ sx: { color: '#888' } }} />
        <FormControlLabel sx={{ color: '#fff' }}
          control={
            <Switch checked={!!cfg.free_for_media_paid}
              onChange={e => updateField('free_for_media_paid', e.target.checked)} />
          }
          label="Безкоштовно для активних Media-передплатників" />
      </Stack>

      <Divider sx={{ borderColor: '#444' }} />

      {/* Промо — спрощено: один UA textarea, бекенд робить uk→en fallback */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="subtitle2" sx={{ color: 'var(--ac1, #7c4dff)' }}>
          🎁 Промо-рядок («для перших N»)
        </Typography>
        <FormControlLabel sx={{ color: '#fff' }}
          control={
            <Switch checked={!!cfg.promo_enabled}
              onChange={e => updateField('promo_enabled', e.target.checked)} />
          }
          label="Показувати" />
        <TextField label="Цільова кількість (N)" type="number" size="small"
          value={cfg.promo_target_count || 0}
          onChange={e => updateField('promo_target_count', parseInt(e.target.value) || 0)} />
      </Stack>
      <TextField label="Промо-текст (українською)"
        multiline minRows={2} fullWidth size="small"
        placeholder="Напр.: для перших 300 учасників"
        helperText="Введи лише українською — для інших мов буде показано той самий текст (авто-переклад додамо пізніше)."
        FormHelperTextProps={{ sx: { color: '#888' } }}
        value={promoUk}
        onChange={e => setPromoUk(e.target.value)} />

      <Divider sx={{ borderColor: '#444' }} />

      <Typography variant="subtitle2" sx={{ color: 'var(--ac1, #7c4dff)' }}>
        💳 Реквізити для оплати
      </Typography>
      <Alert severity="warning" sx={{ '& .MuiAlert-message': { color: '#fff' } }}>
        🚧 TODO L.1.7: автопідтягувати реквізити з Media-проекту. Поки введи вручну українською.
      </Alert>
      <TextField label="Реквізити (українською)"
        multiline minRows={3} fullWidth size="small"
        value={requisitesUk}
        onChange={e => setRequisitesUk(e.target.value)} />

      <Divider sx={{ borderColor: '#444' }} />
      <Typography variant="subtitle2" sx={{ color: 'var(--ac1, #7c4dff)' }}>
        📝 Тексти на екранах (UA — авто-переклад на інші мови)
      </Typography>
      {/* D-258: фото над текстом вітання бота (логотип/банер) */}
      <Typography variant="caption" sx={{ color: '#bbb' }}>
        🖼 Фото вітання — показується <b>над</b> текстом при першому /start. PNG / JPG / WebP · макс 5 МБ.
        Якщо не завантажено — бот візьме логотип додатку.
      </Typography>
      <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <Box sx={{
            width: 96, height: 54, borderRadius: '8px', overflow: 'hidden',
            bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.12)', flexShrink: 0,
          }}>
            {welImg
              ? <img src={welImg} alt="welcome" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 13, color: '#888' }}>лого</span>}
          </Box>
          {welImg
            ? <Chip label={welImg.split('/').pop()} size="small" sx={{ bgcolor: '#1a3a1a', color: '#4ade80' }} />
            : <Chip label="не завантажено (буде логотип)" size="small" sx={{ bgcolor: '#2a2a2a', color: '#888' }} />}
          {welImg && (
            <Button size="small" variant="outlined" color="error" onClick={deleteWelImg}>
              🗑 Видалити
            </Button>
          )}
          <Button size="small" component="label" variant="contained"
            disabled={welImgUploading} sx={{ bgcolor: '#7c3aed' }}>
            {welImgUploading ? '⏳' : '📁 Завантажити фото'}
            <input type="file" hidden accept=".png,.jpg,.jpeg,.webp"
              onChange={e => { if (e.target.files[0]) uploadWelImg(e.target.files[0]); e.target.value=''; }} />
          </Button>
        </Stack>
      </Paper>
      {welImgMsg && <Alert severity={welImgMsg.type} sx={{ mt: 0.5 }}>{welImgMsg.text}</Alert>}
      {/* D-257: текст вітання бота (надсилається+закріплюється на 1-й /start) */}
      <TextField label="💬 Вітання бота (текст після «Привіт!»)"
        multiline minRows={5} fullWidth size="small"
        helperText="Шлеться при першому /start і ЗАКРІПЛЮЄТЬСЯ у чаті. Пиши українською — бот САМ перекладе на мову пристрою юзера. HTML: <b>жирний</b>. «Привіт[, ім'я]!» додається автоматично зверху. Порожнє → дефолт. Після правки → «📌 Закріпити вітання» у Users, щоб оновити в існуючих."
        FormHelperTextProps={{ sx: { color: '#888' } }}
        value={(cfg.welcome_body || '')}
        onChange={e => updateField('welcome_body', e.target.value)} />
      <TextField label="Tagline (на TrialScreen + DonateScreen)"
        multiline minRows={1} fullWidth size="small"
        helperText="Напр.: Один донат — доступ на 1 рік"
        FormHelperTextProps={{ sx: { color: '#888' } }}
        value={(cfg.tagline_uk || '')}
        onChange={e => updateField('tagline_uk', e.target.value)} />
      <TextField label="Текст: Trial screen"
        multiline minRows={2} fullWidth size="small"
        value={(cfg.screen_hint_trial_uk || '')}
        onChange={e => updateField('screen_hint_trial_uk', e.target.value)} />
      <TextField label="Текст: Active screen (купив донат)"
        multiline minRows={2} fullWidth size="small"
        value={(cfg.screen_hint_active_uk || '')}
        onChange={e => updateField('screen_hint_active_uk', e.target.value)} />
      <TextField label="Текст: Expired screen (Trial завершився, немає Media)"
        multiline minRows={2} fullWidth size="small"
        value={(cfg.screen_hint_expired_uk || '')}
        onChange={e => updateField('screen_hint_expired_uk', e.target.value)} />
      <TextField label="Текст: Free via Media screen (активна Media-підписка)"
        multiline minRows={2} fullWidth size="small"
        value={(cfg.screen_hint_free_via_media_uk || '')}
        onChange={e => updateField('screen_hint_free_via_media_uk', e.target.value)} />

      <Divider sx={{ borderColor: '#444' }} />
      <Typography variant="subtitle2" sx={{ color: 'var(--ac1, #7c4dff)' }}>🤖 LLM / TTS</Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <TextField select label="LLM provider" size="small" sx={{ minWidth: 160 }}
          value={cfg.llm_provider || 'anthropic'} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          onChange={e => updateField('llm_provider', e.target.value)}>
          {['anthropic', 'openai'].map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField label="LLM model" size="small" sx={{ minWidth: 280 }}
          value={cfg.llm_model || ''}
          placeholder="claude-haiku-4-5-20251001"
          helperText="e.g. claude-haiku-4-5-20251001, gpt-4o-mini"
          onChange={e => updateField('llm_model', e.target.value)} />
        <TextField select label="TTS engine" size="small" sx={{ minWidth: 160 }}
          value={cfg.tts_engine || ''} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          onChange={e => updateField('tts_engine', e.target.value)}>
          {['edge-tts', 'google', 'openai', 'elevenlabs']
            .map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
      </Stack>

      <Divider sx={{ borderColor: '#444' }} />
      <Typography variant="subtitle2" sx={{ color: 'var(--ac1, #7c4dff)' }}>🖼 Логотип додатку</Typography>
      <Typography variant="caption" sx={{ color: '#888' }}>
        PNG / JPG / SVG / WebP · макс 2 МБ · рекомендований розмір 200×200 px
      </Typography>
      <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          {/* Preview */}
          <Box sx={{
            width: 48, height: 48, borderRadius: '10px', overflow: 'hidden',
            bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.12)', flexShrink: 0,
          }}>
            {logo
              ? <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <span style={{ fontSize: 24 }}>💎</span>}
          </Box>
          {logo
            ? <Chip label={logo.split('/').pop()} size="small" sx={{ bgcolor: '#1a3a1a', color: '#4ade80' }} />
            : <Chip label="не завантажено (💎 за замовч.)" size="small" sx={{ bgcolor: '#2a2a2a', color: '#888' }} />}
          {logo && (
            <Button size="small" variant="outlined" color="error" onClick={deleteLogo}>
              🗑 Видалити
            </Button>
          )}
          <Button size="small" component="label" variant="contained"
            disabled={logoUploading} sx={{ bgcolor: '#7c3aed' }}>
            {logoUploading ? '⏳' : '📁 Завантажити'}
            <input type="file" hidden accept=".png,.jpg,.jpeg,.svg,.webp"
              onChange={e => { if (e.target.files[0]) uploadLogo(e.target.files[0]); e.target.value=''; }} />
          </Button>
        </Stack>
      </Paper>
      {logoMsg && <Alert severity={logoMsg.type} sx={{ mt: 1 }}>{logoMsg.text}</Alert>}

      {soundMsg && <Alert severity={soundMsg.type} sx={{ mt: 1 }}>{soundMsg.text}</Alert>}

      <Divider sx={{ borderColor: '#444' }} />
      <Typography variant="subtitle2" sx={{ color: 'var(--ac1, #7c4dff)' }}>📢 Admin notify</Typography>
      <Stack direction="row" spacing={2}>
        <TextField label="Alerts chat_id" size="small"
          value={cfg.admin_alerts_chat_id || ''}
          onChange={e => updateField('admin_alerts_chat_id', e.target.value)} />
        <TextField label="Thread id" type="number" size="small"
          value={cfg.admin_alerts_thread_id || ''}
          onChange={e => updateField('admin_alerts_thread_id', parseInt(e.target.value) || null)} />
      </Stack>

      {msg && <Alert severity={msg.type}>{msg.text}</Alert>}
      <Button variant="contained" disabled={saving}
        sx={{ bgcolor: 'var(--ac1, #7c4dff)', alignSelf: 'flex-start' }} onClick={save}>
        {saving ? '⏳ Зберігаю...' : '💾 Зберегти'}
      </Button>
    </Stack>
  );
}

// =============================================================================
// DonationsTab — як L.1.5 (без змін)
// =============================================================================
function DonationsTab({ onCountChange }) {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // 💎 Security fix (D-156): the receipt endpoint now requires verify_token, but an
  // <img src> can't send the Bearer header → 401 → broken image. Fetch the file as a
  // blob WITH the token and render via object URL (works for images and PDF).
  const openReceipt = async (url) => {
    setPreviewLoading(true); setPreviewIsPdf(false);
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` },
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      setPreviewIsPdf((blob.type || '').includes('pdf'));
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      alert('Не вдалося завантажити квитанцію: ' + e.message);
    } finally { setPreviewLoading(false); }
  };
  const closePreview = () => {
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch {} }
    setPreviewUrl(null); setPreviewIsPdf(false);
  };

  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/donations?status=${filter}`)
      .then(r => { setList(r.donations || []); onCountChange?.(); });
  }, [filter, onCountChange]);
  useEffect(load, [load]);
  // D-252: відкрив вкладку → авто-оплати картками позначаємо переглянутими (ховаємо 🆕-бейдж)
  useEffect(() => {
    jsonFetch(`${API_BASE}/donations/mark-seen`, { method: 'POST', body: '{}' })
      .then(() => onCountChange?.()).catch(() => {});
  }, []);

  const approve = async (id) => {
    setBusy(true);
    try {
      await jsonFetch(`${API_BASE}/donations/${id}/approve`, {
        method: 'POST', body: JSON.stringify({}),
      });
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await jsonFetch(`${API_BASE}/donations/${rejectFor}/reject`, {
        method: 'POST', body: JSON.stringify({ reject_reason: rejectReason }),
      });
      setRejectFor(null); setRejectReason(''); load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Видалити донат і файл-квитанцію назавжди?')) return;
    setBusy(true);
    try {
      await jsonFetch(`${API_BASE}/donations/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <Stack spacing={2} sx={INPUT_SX}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ color: '#fff' }}>💰 Донати</Typography>
        <Chip label="💎 PlayLanguage" sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.25)', color: '#c4b5fd' }} />
        <Box flex={1} />
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <Chip key={s} label={s} onClick={() => setFilter(s)}
                variant={filter === s ? 'filled' : 'outlined'}
                sx={{ color: filter === s ? '#fff' : '#aaa',
                      bgcolor: filter === s ? 'var(--ac1, #7c4dff)' : 'transparent',
                      borderColor: '#555' }} />
        ))}
      </Stack>

      {list.length === 0 && (
        <Alert severity="info">Список порожній для status="{filter}"</Alert>
      )}

      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['App','ID','User','€','Status','Created','Receipt','Actions'].map(c =>
                <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(d => (
              <TableRow key={d.id} hover sx={{ '& td': { color: '#fff' } }}>
                <TableCell>
                  <Chip size="small" label={d.app_badge}
                        sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.25)', color: '#c4b5fd' }} />
                </TableCell>
                <TableCell>#{d.id}</TableCell>
                <TableCell>
                  {d.first_name || '—'}
                  {d.username && <span style={{ color: '#aaa' }}> @{d.username}</span>}
                  <br />
                  <span style={{ color: '#888', fontSize: '0.75rem' }}>
                    tg:{d.telegram_id} db:{d.user_id}
                  </span>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{fmtEur(d.amount_eur)}</TableCell>
                <TableCell>
                  {/* D-252: mono → ясний статус (авто-картка готово / очікує оплати), не плутати з «треба Approve» */}
                  <Chip size="small"
                        label={d.method === 'mono'
                          ? (d.status === 'approved' ? '💳 Авто-картка ✅'
                             : d.status === 'pending' ? '⏳ очікує оплати'
                             : d.status)
                          : d.status}
                        color={d.status === 'approved' ? 'success'
                             : d.status === 'rejected' ? 'error'
                             : (d.status === 'pending' && d.method !== 'mono') ? 'warning' : 'default'} />
                  {d.reject_reason && (
                    <Tooltip title={d.reject_reason}>
                      <Typography variant="caption" sx={{ color: '#fca5a5', display: 'block' }}>
                        ✗ {d.reject_reason.slice(0, 30)}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ color: '#fff' }}>{fmtDate(d.created_at)}</Typography>
                  {d.reviewed_at && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                      ✓ {fmtDate(d.reviewed_at)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {d.has_receipt
                    ? <Button size="small" disabled={previewLoading} onClick={() => openReceipt(d.receipt_url)}
                              sx={{ color: 'var(--ac2, #a78bfa)' }}>👁 Перегляд</Button>
                    : <Typography variant="caption" sx={{ color: '#888' }}>—</Typography>}
                </TableCell>
                <TableCell align="right">
                  {/* D-252: ручні Approve/Reject — лише для чек-донатів. mono-pending авто-резолвиться. */}
                  {d.status === 'pending' && d.method !== 'mono' && (
                    <>
                      <Button size="small" color="success" disabled={busy}
                        onClick={() => approve(d.id)}>✅ Approve</Button>
                      <Button size="small" color="error" disabled={busy}
                        onClick={() => setRejectFor(d.id)}>❌ Reject</Button>
                    </>
                  )}
                  {d.status === 'pending' && d.method === 'mono' && (
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mr: 1 }}>
                      оплата ще не надійшла<br />— дій не треба
                    </Typography>
                  )}
                  {d.status === 'approved' && d.method === 'mono' && d.auto_confirmed && (
                    <Typography variant="caption" sx={{ color: '#5eead4', display: 'block', mr: 1 }}>
                      ✅ авто, без участі
                    </Typography>
                  )}
                  <IconButton size="small" disabled={busy} onClick={() => del(d.id)}
                    title="Видалити" sx={{ color: '#888' }}>🗑</IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!previewUrl} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>📎 Квитанція</DialogTitle>
        <DialogContent>
          {previewUrl && (previewIsPdf
            ? <iframe src={previewUrl} title="receipt"
                      style={{ width: '100%', height: '80vh', border: 'none' }} />
            : <img src={previewUrl} alt="receipt"
                   style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectFor} onClose={() => setRejectFor(null)}>
        <DialogTitle>Відхилити донат #{rejectFor}</DialogTitle>
        <DialogContent sx={INPUT_SX}>
          <TextField autoFocus fullWidth multiline minRows={2} sx={{ mt: 1 }}
            label="Причина (юзер побачить це повідомленням)"
            value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectFor(null)}>Скасувати</Button>
          <Button color="error" disabled={busy || !rejectReason} onClick={reject}>
            Відхилити
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// IncomeTab + ExpensesTab + PLACEHOLDER + MAIN — як у L.1.5
function IncomeTab() {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  useEffect(() => { jsonFetch(`${API_BASE}/income?period=${period}`).then(setData); }, [period]);
  if (!data) return <CircularProgress />;
  const maxSum = Math.max(1, ...data.by_day.map(d => d.sum));
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1}>
        <Typography variant="h6" sx={{ color: '#fff' }}>💵 Дохід</Typography>
        <Box flex={1} />
        {['7d','30d','90d','365d'].map(p =>
          <Chip key={p} label={p} onClick={() => setPeriod(p)}
            variant={period === p ? 'filled' : 'outlined'}
            sx={{ bgcolor: period === p ? 'var(--ac1, #7c4dff)' : 'transparent',
                  color: period === p ? '#fff' : '#aaa', borderColor: '#555' }} />)}
      </Stack>
      <Stack direction="row" spacing={2}>
        <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.10)' }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>За період</Typography>
          <Typography variant="h4" sx={{ color: 'var(--ac1, #7c4dff)', fontWeight: 'bold' }}>
            {fmtEur(data.by_day.reduce((s, d) => s + d.sum, 0))}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(94,234,212,0.10)' }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>Всього (all time)</Typography>
          <Typography variant="h4" sx={{ color: '#5eead4', fontWeight: 'bold' }}>
            {fmtEur(data.all_time_eur)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>{data.all_time_count} донатів</Typography>
        </Paper>
      </Stack>
      <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: '#aaa' }}>Дохід по днях</Typography>
        {data.by_day.length === 0
          ? <Typography variant="caption" sx={{ color: '#888' }}>Немає approved донатів</Typography>
          : data.by_day.map(d => (
              <Stack key={d.date} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ width: 90, color: '#aaa' }}>{d.date}</Typography>
                <Box sx={{ flex: 1, bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.5)', height: 18,
                           width: `${(d.sum / maxSum) * 100}%`, borderRadius: 1 }} />
                <Typography variant="caption" sx={{ width: 60, textAlign: 'right', color: '#fff' }}>
                  {fmtEur(d.sum)}
                </Typography>
              </Stack>
            ))}
      </Paper>
    </Stack>
  );
}

// 💎 PL Phase E: live maturity countdown helper (target = ISO ts).
function fmtCD(targetIso) {
  if (!targetIso) return '—';
  const diff = new Date(targetIso).getTime() - Date.now();
  if (isNaN(diff)) return '—';
  if (diff <= 0) return 'доступно';
  const total = Math.floor(diff / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return (d > 0 ? `${d}д ` : '') + `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function CountdownCell({ target, matured }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (matured || !target) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [target, matured]);
  if (matured) return <Chip size="small" label="✅ доступно" sx={{ bgcolor: 'rgba(74,222,128,0.18)', color: '#4ade80' }} />;
  return <Chip size="small" label={`⏳ ${fmtCD(target)}`} sx={{ bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }} />;
}

function ReferralEarningsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    jsonFetch(`${API_BASE}/referral-earnings?limit=200`)
      .then(setData).catch((e) => setErr(String(e.message || e)));
  }, []);
  const rows = (data && data.earnings) || [];
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="💎 Реферальний дохід PlayLanguage" sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.25)', color: '#c4b5fd' }} />
        {data && <Chip label={`Утримується: ${fmtEur(data.held_total_eur)}`} sx={{ bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24' }} />}
        {data && <Chip label={`Доступно: ${fmtEur(data.available_total_eur)}`} sx={{ bgcolor: 'rgba(74,222,128,0.18)', color: '#4ade80' }} />}
      </Box>
      <Typography sx={{ color: '#888', fontSize: 13, mb: 2 }}>
        20% з кожного донату запрошеного друга. Сума «дозріває» {(data && data.hold_days) || 14} днів (відлік нижче), потім стає доступною до виводу.
      </Typography>
      {err && <Typography sx={{ color: '#ff8a80', mb: 2 }}>Помилка: {err}</Typography>}
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['#', 'Реферер', 'Від кого', 'Донат', '%', 'Нараховано', 'Дата', 'Активація / відлік'].map((c) =>
                <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} sx={{ color: '#777' }}>Поки немає нарахувань.</TableCell></TableRow>
            ) : rows.map((e) => (
              <TableRow key={e.id} hover sx={{ '& td': { color: '#fff' } }}>
                <TableCell>#{e.id}</TableCell>
                <TableCell>{e.referrer}</TableCell>
                <TableCell>{e.referred}</TableCell>
                <TableCell>{fmtEur(e.gross_eur)}</TableCell>
                <TableCell>{e.percent_applied}%</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#4ade80' }}>{fmtEur(e.amount_eur)}</TableCell>
                <TableCell>{fmtDate(e.created_at)}</TableCell>
                <TableCell><CountdownCell target={e.mature_at} matured={e.matured} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function ExpensesTab() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [period, setPeriod] = useState('30d');
  const [form, setForm] = useState({ category: '', amount_eur: '', note: '', expense_date: '' });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/expenses?period=${period}`).then(r => {
      setList(r.expenses); setTotal(r.all_time_total_eur);
    });
  }, [period]);
  useEffect(load, [load]);
  const submit = async () => {
    if (!form.category || !form.amount_eur) { alert('Категорія + сума обов\'язкові'); return; }
    setBusy(true);
    try {
      await jsonFetch(`${API_BASE}/expenses`, {
        method: 'POST',
        body: JSON.stringify({
          ...form, amount_eur: parseFloat(form.amount_eur),
          expense_date: form.expense_date || null,
        }),
      });
      setForm({ category: '', amount_eur: '', note: '', expense_date: '' });
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Видалити запис?')) return;
    await jsonFetch(`${API_BASE}/expenses/${id}`, { method: 'DELETE' });
    load();
  };
  return (
    <Stack spacing={2} sx={INPUT_SX}>
      <Stack direction="row" spacing={1}>
        <Typography variant="h6" sx={{ color: '#fff' }}>📊 Затрати</Typography>
        <Box flex={1} />
        {['7d','30d','90d','365d','all'].map(p =>
          <Chip key={p} label={p} onClick={() => setPeriod(p)}
            variant={period === p ? 'filled' : 'outlined'}
            sx={{ bgcolor: period === p ? 'var(--ac1, #7c4dff)' : 'transparent',
                  color: period === p ? '#fff' : '#aaa', borderColor: '#555' }} />)}
      </Stack>
      <Paper sx={{ p: 2, bgcolor: 'rgba(239,68,68,0.10)' }}>
        <Typography variant="caption" sx={{ color: '#aaa' }}>Всього затрат (all time)</Typography>
        <Typography variant="h5" sx={{ color: '#fca5a5', fontWeight: 'bold' }}>{fmtEur(total)}</Typography>
      </Paper>
      <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#aaa' }}>+ Додати запис</Typography>
        <Stack direction="row" spacing={1}>
          <TextField label="Дата" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={form.expense_date}
            onChange={e => setForm(p => ({ ...p, expense_date: e.target.value }))} />
          <TextField select label="Категорія" size="small" sx={{ minWidth: 160 }}
            value={form.category} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
            onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
            {['LLM API','TTS API','Hosting','Volume','Інше']
              .map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="€" type="number" size="small" sx={{ width: 100 }}
            value={form.amount_eur}
            onChange={e => setForm(p => ({ ...p, amount_eur: e.target.value }))} />
          <TextField label="Примітка" size="small" fullWidth
            value={form.note}
            onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
          <Button variant="contained" disabled={busy} onClick={submit}
            sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>+ Додати</Button>
        </Stack>
      </Paper>
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Дата','Категорія','€','Примітка',''].map(c =>
                <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(e => (
              <TableRow key={e.id} sx={{ '& td': { color: '#fff' } }}>
                <TableCell>{e.expense_date}</TableCell>
                <TableCell>{e.category}</TableCell>
                <TableCell>{fmtEur(e.amount_eur)}</TableCell>
                <TableCell>{e.note || '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => del(e.id)}
                    sx={{ color: '#888' }}>🗑</IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}


// =============================================================================
// L.1.8 SecurityTab — incidents + blocked users management
// =============================================================================
function SecurityTab({ onCountChange }) {
  const [incidents, setIncidents] = useState([]);
  const [blocked, setBlocked]   = useState([]);
  const [filter, setFilter]     = useState('open');
  const [busy, setBusy]         = useState(false);

  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/security/incidents?status=${filter}`)
      .then(r => { setIncidents(r.incidents || []); onCountChange?.(); });
    jsonFetch(`${API_BASE}/security/blocked`).then(r => setBlocked(r.blocked || []));
  }, [filter, onCountChange]);
  useEffect(load, [load]);

  const resolve = async (id) => {
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/incidents/${id}/resolve`, { method: 'POST', body: '{}' }); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const blockPerm = async (uid) => {
    if (!window.confirm('Заблокувати назавжди?')) return;
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/block`,
      { method: 'POST', body: JSON.stringify({ user_id: uid, hours: null, reason: 'admin permanent' }) });
      load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const blockTemp = async (uid, hours) => {
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/block`,
      { method: 'POST', body: JSON.stringify({ user_id: uid, hours, reason: `admin temp ${hours}h` }) });
      load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const unblock = async (uid) => {
    setBusy(true);
    try { await jsonFetch(`${API_BASE}/security/unblock/${uid}`, { method: 'POST', body: '{}' }); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <Stack spacing={3} sx={INPUT_SX}>
      {/* INCIDENTS */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ color: '#fff' }}>🛡 Інциденти безпеки</Typography>
        <Box flex={1} />
        {['open', 'resolved', 'all'].map(s =>
          <Chip key={s} label={s} onClick={() => setFilter(s)}
            variant={filter === s ? 'filled' : 'outlined'}
            sx={{ bgcolor: filter === s ? 'var(--ac1, #7c4dff)' : 'transparent',
                  color: filter === s ? '#fff' : '#aaa', borderColor: '#555' }} />)}
      </Stack>
      {incidents.length === 0 && <Alert severity="info">Немає інцидентів зі статусом "{filter}"</Alert>}
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['ID','Час','User','Тип','Деталі','IP','Block?','Actions'].map(c =>
                <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {incidents.map(i => (
              <TableRow key={i.id} sx={{ '& td': { color: '#fff' } }}>
                <TableCell>#{i.id}</TableCell>
                <TableCell>{fmtDate(i.created_at)}</TableCell>
                <TableCell>
                  {i.username ? `@${i.username}` : '—'}
                  <br />
                  <span style={{ color: '#888', fontSize: '0.75rem' }}>
                    tg:{i.telegram_id} db:{i.user_id || '?'}
                  </span>
                </TableCell>
                <TableCell><Chip size="small" label={i.type} color="warning" /></TableCell>
                <TableCell>
                  <Tooltip title={JSON.stringify(i.details || {}, null, 2)}>
                    <Typography variant="caption" sx={{ color: '#fca5a5' }}>
                      {String(i.details?.reason || i.details?.filename || '').slice(0, 40)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ color: '#888' }}>{i.ip || '—'}</Typography>
                </TableCell>
                <TableCell>
                  {i.is_blocked
                    ? <Chip size="small" label={i.blocked_until ? `до ${fmtDate(i.blocked_until)}` : 'назавжди'}
                            color="error" />
                    : <span style={{ color: '#888' }}>—</span>}
                </TableCell>
                <TableCell align="right">
                  {i.status === 'open' &&
                    <Button size="small" disabled={busy} onClick={() => resolve(i.id)}
                            sx={{ color: '#5eead4' }}>✓ Resolve</Button>}
                  {i.user_id && !i.is_blocked && (
                    <>
                      <Button size="small" disabled={busy} onClick={() => blockTemp(i.user_id, 1)}
                              sx={{ color: '#fbbf24' }}>1h</Button>
                      <Button size="small" disabled={busy} onClick={() => blockTemp(i.user_id, 24)}
                              sx={{ color: '#fb923c' }}>24h</Button>
                      <Button size="small" disabled={busy} onClick={() => blockPerm(i.user_id)}
                              sx={{ color: '#ef4444' }}>∞</Button>
                    </>
                  )}
                  {i.user_id && i.is_blocked && (
                    <Button size="small" disabled={busy} onClick={() => unblock(i.user_id)}
                            sx={{ color: '#5eead4' }}>🔓 Unblock</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* BLOCKED USERS */}
      <Divider sx={{ borderColor: '#444' }} />
      <Typography variant="h6" sx={{ color: '#fff' }}>🚫 Заблоковані юзери ({blocked.length})</Typography>
      {blocked.length === 0
        ? <Alert severity="success">Зараз нікого не заблоковано.</Alert>
        : (
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['User','Заблокован','До','Причина','Actions'].map(c =>
                    <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {blocked.map(b => (
                  <TableRow key={b.user_id} sx={{ '& td': { color: '#fff' } }}>
                    <TableCell>
                      {b.username ? `@${b.username}` : '—'}
                      <br />
                      <span style={{ color: '#888', fontSize: '0.75rem' }}>
                        tg:{b.telegram_id} db:{b.user_id}
                      </span>
                    </TableCell>
                    <TableCell>{fmtDate(b.blocked_at)}</TableCell>
                    <TableCell>
                      {b.permanent
                        ? <Chip size="small" label="назавжди" color="error" />
                        : fmtDate(b.blocked_until)}
                    </TableCell>
                    <TableCell><Typography variant="caption">{b.reason}</Typography></TableCell>
                    <TableCell align="right">
                      <Button size="small" disabled={busy} onClick={() => unblock(b.user_id)}
                              sx={{ color: '#5eead4' }}>🔓 Unblock</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
    </Stack>
  );
}


const PLACEHOLDER = (text) => (
  <Paper sx={{ p: 2, bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.05)', border: '1px dashed var(--ac1, #7c4dff)' }}>
    <Typography variant="caption" sx={{ color: 'var(--ac1, #7c4dff)' }}>🚧 {text}</Typography>
  </Paper>
);


// ─── Users Tab (L.3) ────────────────────────────────────────────────────────
// 💎 PlayLanguage: list provisioned PL users + their effective access-until.
// The "access until" value comes from the SAME _pl_effective_access the mini app
// uses, so what the admin sees here == what the user sees on their start screen.
const fmtDateShort = (s) => s ? new Date(s).toLocaleDateString('uk-UA') : '—';
const PL_SOURCE_LABEL = {
  trial: '🎁 Тріал', donation: '💰 Донат', manual: '✋ Вручну',
  forever: '♾️ Назавжди', media: '📺 Media',
};

// 💎 Phase F: per-user manual access controls.
// «Додати» extends the user's effective «Доступ до» date by N days (server bases
// the new window on the current end date, so the visible date moves forward by N).
// «♾️» toggles pl_forever. «↩️» revokes ONLY admin-granted access (manual days +
// forever) — paid donations and trial are left intact server-side.
function UserActions({ u, onUpdated }) {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  // «Додати» is the save button — show a visible success flash so it's obvious
  // the days were applied; the server also DMs the user that N days were added.
  const [flash, setFlash] = useState('');
  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (r && r.access) onUpdated(r.access); if (okMsg) showFlash(okMsg); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  const grant = () => {
    const n = parseInt(days, 10);
    if (!n || n < 1) { alert('Вкажи кількість днів (≥1)'); return; }
    if (!window.confirm(`Додати ${n} дн. до доступу цього юзера?\n(дата «Доступ до» зсунеться на ${n} дн. вперед)`)) return;
    run(() => jsonFetch(`${API_BASE}/users/${u.user_id}/grant`,
      { method: 'POST', body: JSON.stringify({ days: n }) }), `✓ +${n} дн.`);
  };
  const toggleForever = () => run(() => jsonFetch(`${API_BASE}/users/${u.user_id}/forever`,
    { method: 'POST', body: JSON.stringify({ forever: !u.forever }) }), !u.forever ? '✓ Назавжди' : '✓ Вимкнено');
  const revoke = () => {
    if (!window.confirm('Скинути ручний доступ (видані дні + «назавжди»)?\nОплачені донати й тріал НЕ чіпаються.')) return;
    run(() => jsonFetch(`${API_BASE}/users/${u.user_id}/revoke`, { method: 'POST' }), '✓ Скинуто');
  };
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TextField size="small" type="number" value={days} disabled={busy}
        onChange={e => setDays(e.target.value)} sx={{ width: 60 }}
        inputProps={{ min: 1, max: 3650, style: { padding: '4px 6px' } }} />
      <Tooltip title="Додати ці дні до дати «Доступ до» (юзеру прийде повідомлення)">
        <span><Button size="small" variant="contained" disabled={busy} onClick={grant}
          sx={{ minWidth: 56, px: 0.8, bgcolor: 'var(--ac1, #7c4dff)', '&:hover': { bgcolor: '#6a3df0' } }}>Додати</Button></span>
      </Tooltip>
      <Tooltip title={u.forever ? 'Вимкнути «назавжди»' : 'Дати доступ назавжди'}>
        <span><Switch size="small" checked={!!u.forever} disabled={busy} onChange={toggleForever} /></span>
      </Tooltip>
      <Tooltip title="Скинути ручний доступ (видані дні + «назавжди»)">
        <span><IconButton size="small" disabled={busy} onClick={revoke}
          sx={{ color: '#ff8a80' }}>↩️</IconButton></span>
      </Tooltip>
      {flash && <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{flash}</Typography>}
    </Stack>
  );
}

// 💎 PlayLanguage — DM one user / broadcast to all reachable. Sends via @PlayLanguage_bot.
function MessageDialog({ open, dmUser, reachableCount, onClose }) {
  const isBroadcast = !dmUser;
  const [msg, setMsg] = useState('');
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setMsg(''); setPhoto(null); setResult(null); setBusy(false); } }, [open]);
  const send = () => {
    const text = msg.trim();
    if (!text && !photo) return;
    if (isBroadcast && !window.confirm(`Надіслати це повідомлення ${reachableCount} досяжним користувачам PlayLanguage?`)) return;
    setBusy(true);
    const url = isBroadcast ? `${API_BASE}/broadcast` : `${API_BASE}/users/${dmUser.user_id}/message`;
    const fd = new FormData();
    fd.append('message', text);
    if (photo) fd.append('file', photo);
    fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` },
      credentials: 'include',
      body: fd,
    })
      .then(async r => { if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`); return r.json(); })
      .then(r => setResult(r))
      .catch(e => setResult({ error: e.message }))
      .finally(() => setBusy(false));
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#1a0a2e', color: '#fff' } }}>
      <DialogTitle sx={{ color: '#fff' }}>
        {isBroadcast
          ? `📢 Повідомлення всім PL (досяжно: ${reachableCount})`
          : `✉️ ${dmUser.username ? '@' + dmUser.username : 'tg:' + dmUser.telegram_id}`}
      </DialogTitle>
      <DialogContent>
        {isBroadcast && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Отримають лише користувачі, активні у @PlayLanguage_bot. Текст підтримує HTML:
            {' '}&lt;b&gt;жирний&lt;/b&gt;, &lt;i&gt;курсив&lt;/i&gt;.
          </Alert>
        )}
        <TextField multiline minRows={4} fullWidth autoFocus
          placeholder="Текст повідомлення…" value={msg}
          onChange={e => setMsg(e.target.value)} sx={INPUT_SX} />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <Button component="label" size="small" variant="outlined"
            sx={{ color: '#c4b5fd', borderColor: 'var(--ac1, #7c4dff)', whiteSpace: 'nowrap' }}>
            🖼 {photo ? 'Замінити фото' : 'Додати фото'}
            <input hidden type="file" accept="image/*"
              onChange={e => setPhoto(e.target.files?.[0] || null)} />
          </Button>
          {photo && (
            <>
              <Box component="img" src={URL.createObjectURL(photo)} alt=""
                sx={{ height: 44, width: 44, objectFit: 'cover', borderRadius: 1, border: '1px solid #444' }} />
              <Typography variant="caption" sx={{ color: '#bbb', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo.name}</Typography>
              <IconButton size="small" onClick={() => setPhoto(null)} sx={{ color: '#ff8a80' }}>✖️</IconButton>
            </>
          )}
        </Stack>
        {result && (
          <Alert severity={result.error ? 'error' : 'success'} sx={{ mt: 2 }}>
            {result.error
              ? result.error
              : isBroadcast
                ? `✅ Надіслано: ${result.sent} · ❌ Невдало: ${result.failed} · Усього: ${result.total}`
                : '✅ Повідомлення надіслано'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#aaa' }}>Закрити</Button>
        <Button onClick={send} disabled={busy || (!msg.trim() && !photo)} variant="contained"
          sx={{ bgcolor: 'var(--ac1, #7c4dff)', '&:hover': { bgcolor: '#6a3de8' } }}>
          {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (isBroadcast ? '📢 Розіслати' : 'Надіслати')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 💎 2026-09-11: КАРТКА ЮЗЕРА. Раніше клік по рядку не давав нічого: щоб побачити
// картину по людині, доводилось читати вісім вузьких колонок, а дії тулились у
// крайню комірку. Компонент оголошено на рівні модуля (не всередині UsersTab) —
// вкладений компонент React вважає НОВИМ типом і перемонтовує на кожен рендер,
// через що поле «днів» скидалось би під час набору.
function PlUserCard({ u, onClose, onUpdated, onMessage }) {
  const [days, setDays] = useState('30');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const info = (k, v) => (
    <Stack key={k} direction="row" justifyContent="space-between" spacing={2}
      sx={{ borderBottom: '1px solid rgba(var(--ac1rgb, 124, 77, 255),0.12)', pb: 0.7 }}>
      <Typography sx={{ color: '#9a92c4', fontSize: 13.5, whiteSpace: 'nowrap' }}>{k}</Typography>
      <Typography sx={{ color: '#eee9ff', fontSize: 13.5, textAlign: 'right' }}>{v}</Typography>
    </Stack>
  );

  const accessText = () => {
    if (u.forever) return '♾️ Назавжди';
    return (u.has_access ? '✅ до ' : '⛔ з ') + fmtDateShort(u.access_until);
  };

  const gift = async () => {
    const n = parseInt(days, 10);
    if (!n || n < 1) { setMsg('❌ Вкажи кількість днів (≥1)'); return; }
    setBusy(true); setMsg('…');
    try {
      const r = await jsonFetch(`${API_BASE}/users/${u.user_id}/grant`,
        { method: 'POST', body: JSON.stringify({ days: n, notify }) });
      let m = '✅ Додано ' + n + ' дн.';
      if (r.access) { m += ' · доступ до ' + fmtDateShort(r.access.access_until); onUpdated && onUpdated(r.access); }
      // Чесно кажемо, що сталося з вітанням: «додано» без цього рядка виглядає
      // як повний успіх навіть тоді, коли юзер нічого не отримав.
      if (!notify) m += ' · без вітання';
      else if (r.notified) m += ' · вітання надіслано' + (r.notify_how === 'photo' ? ' 🖼' : ' (текстом)') + (r.notify_effect ? ' 🎉' : '');
      else m += ' · ⚠️ вітання НЕ дійшло: ' + (r.notify_error || 'невідома причина');
      setMsg(m);
    } catch (e) { setMsg('❌ ' + e.message); }
    setBusy(false);
  };

  const toggleForever = async () => {
    setBusy(true);
    try {
      const r = await jsonFetch(`${API_BASE}/users/${u.user_id}/forever`,
        { method: 'POST', body: JSON.stringify({ forever: !u.forever }) });
      if (r.access) onUpdated && onUpdated(r.access);
      setMsg(!u.forever ? '✅ Доступ назавжди увімкнено' : '✅ «Назавжди» вимкнено');
    } catch (e) { setMsg('❌ ' + e.message); }
    setBusy(false);
  };

  const revoke = async () => {
    if (!window.confirm('Скинути ручний доступ (видані дні + «назавжди»)?\nОплачені донати й тріал НЕ чіпаються.')) return;
    setBusy(true);
    try {
      const r = await jsonFetch(`${API_BASE}/users/${u.user_id}/revoke`, { method: 'POST' });
      if (r.access) onUpdated && onUpdated(r.access);
      setMsg('✅ Ручний доступ скинуто');
    } catch (e) { setMsg('❌ ' + e.message); }
    setBusy(false);
  };

  return (
    <Dialog open={!!u} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { bgcolor: '#150f28', border: '1px solid rgba(var(--ac1rgb, 124, 77, 255),0.35)', borderRadius: 2 } }}>
      <DialogTitle sx={{ color: '#eee9ff', pb: 1 }}>
        {u.username ? '@' + u.username : 'tg:' + u.telegram_id}
        {u.is_new && <Chip size="small" label="🆕 новий" sx={{ ml: 1, height: 20, bgcolor: 'rgba(255,45,45,0.25)', color: '#ff8a80' }} />}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.1} sx={{ mt: 0.5 }}>
          {info('🆔 Telegram ID', u.telegram_id || '—')}
          {info('📊 Статус', u.status || '—')}
          {info('🎟 Доступ', accessText())}
          {info('🧭 Джерело', PL_SOURCE_LABEL[u.source] || u.source || '—')}
          {info('🗣 Мови', (u.native_lang || '?') + ' → ' + (u.learn_lang || '?'))}
          {info('📚 Слів / XP', (u.words_learned || 0) + ' / ' + (u.xp_total || 0))}
          {info('🧠 Слів у повтореннях', String(u.srs_words != null ? u.srs_words : '—'))}
          {info('🔥 Серія днів', String(u.streak_days || 0))}
          {info('🕐 Остання активність', fmtDateShort(u.last_activity))}
          {info('📅 Реєстрація', fmtDateShort(u.created_at))}
          {info('📣 Досяжний у боті', u.reachable ? 'так' : 'ні — не стартував @PlayLanguage_bot')}
          {(u.quiet || u.learning) && info('🏷 Позначка', u.learning ? '🔥 вчиться (7 днів)' : '😴 зайшов і зник')}

          {/* 🎁 подарунок днів */}
          <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.10)', border: '1px solid rgba(var(--ac1rgb, 124, 77, 255),0.3)' }}>
            <Typography sx={{ color: '#c4b5fd', fontWeight: 700, fontSize: 14, mb: 1 }}>🎁 Подарувати дні доступу</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {[7, 14, 30, 90].map(n => (
                <Chip key={n} label={'+' + n} size="small" clickable onClick={() => setDays(String(n))}
                  sx={{ bgcolor: String(n) === String(days) ? 'rgba(var(--ac1rgb, 124, 77, 255),0.45)' : 'rgba(255,255,255,0.07)',
                    color: String(n) === String(days) ? '#fff' : '#aaa', fontWeight: String(n) === String(days) ? 700 : 400 }} />
              ))}
              <TextField size="small" type="number" value={days} onChange={e => setDays(e.target.value)}
                inputProps={{ min: 1, max: 3650, style: { color: '#fff', width: 64, padding: '6px 8px' } }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' } }} />
              <Button size="small" variant="contained" disabled={busy} onClick={gift}
                sx={{ bgcolor: 'var(--ac1, #7c4dff)', fontWeight: 700, '&:hover': { bgcolor: '#6a3df0' } }}>
                {busy ? '…' : '🎁 Подарувати'}
              </Button>
            </Stack>
            <FormControlLabel sx={{ mt: 0.5, ml: 0 }}
              control={<Switch size="small" checked={notify} onChange={e => setNotify(e.target.checked)} />}
              label={<Typography sx={{ color: '#9a92c4', fontSize: 12.5 }}>🎉 святкове вітання у бот (фото + ефект)</Typography>} />
            {msg && (
              <Typography sx={{ mt: 0.5, fontSize: 13, color: msg.startsWith('✅') ? '#4ade80' : (msg === '…' ? '#888' : '#ff8a80') }}>
                {msg}
              </Typography>
            )}
          </Box>

          {/* решта дій */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <FormControlLabel sx={{ ml: 0 }}
              control={<Switch size="small" checked={!!u.forever} disabled={busy} onChange={toggleForever} />}
              label={<Typography sx={{ color: '#9a92c4', fontSize: 12.5 }}>♾️ Доступ назавжди</Typography>} />
            <Button size="small" disabled={busy} onClick={revoke} sx={{ color: '#ff8a80' }}>↩️ Скинути ручний доступ</Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button disabled={!u.reachable} onClick={() => onMessage && onMessage(u)} sx={{ color: '#5eead4' }}>✉️ Написати</Button>
        <Button onClick={onClose} sx={{ color: 'var(--ac1, #7c4dff)' }}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
}


function UsersTab({ onCountChange }) {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [reachable, setReachable] = useState(0);   // 💎 досяжно для DM (notify_enabled)
  const [msgOpen, setMsgOpen] = useState(false);
  const [dmUser, setDmUser] = useState(null);       // null = broadcast, інакше — конкретний юзер
  // 💎 2026-09-11: набір нових юзерів фіксуємо на ПЕРШОМУ завантаженні — далі ні зміна
  // фільтра, ні позначка «прочитано» його не гасять: 🆕 висить, доки вкладка відкрита.
  const [newIds, setNewIds] = useState(null);
  const seenSent = useRef(false);
  const [card, setCard] = useState(null);   // 💎 картка юзера (клік по рядку)

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ status: filter, search });
    jsonFetch(`${API_BASE}/users?${qs.toString()}`)
      .then(r => {
        const users = r.users || [];
        setList(users); setReachable(r.reachable || 0);
        setNewIds(prev => prev || new Set(users.filter(u => u.is_new).map(u => u.user_id)));
        // «Прочитано» шлемо ПІСЛЯ відповіді, а не паралельно з нею: інакше мітка могла б
        // лягти раніше, ніж сервер порахує is_new, і 🆕 не з'явилось би взагалі.
        if (!seenSent.current) {
          seenSent.current = true;
          jsonFetch(`${API_BASE}/new-users/mark-seen`, { method: 'POST', body: JSON.stringify({ tab: 'users' }) })
            .then(() => onCountChange && onCountChange()).catch(() => {});
        }
      })
      .catch(e => alert(e.message))
      .finally(() => setLoading(false));
  }, [filter, search, onCountChange]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // 💎 Phase F: merge the brief returned by a grant/forever/revoke action into the row.
  const patchUser = useCallback((brief) => {
    setList(prev => prev.map(x => x.user_id === brief.user_id ? { ...x, ...brief } : x));
  }, []);

  const accessCell = (u) => {
    if (u.forever) return <Chip size="small" label="♾️ Назавжди" sx={{ bgcolor: 'rgba(94,234,212,0.20)', color: '#5eead4' }} />;
    if (!u.has_access) return <Chip size="small" label={`⛔ ${fmtDateShort(u.access_until)}`} sx={{ bgcolor: 'rgba(244,67,54,0.18)', color: '#ff8a80' }} />;
    return <Chip size="small" label={`✅ до ${fmtDateShort(u.access_until)}`} sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.20)', color: '#c4b5fd' }} />;
  };

  return (
    <Stack spacing={2} sx={INPUT_SX}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" sx={{ color: '#fff' }}>👥 Users</Typography>
        {/* D-254: кількість юзерів (поточний фільтр/пошук) */}
        <Chip size="small" label={loading ? '…' : list.length}
          sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.25)', color: '#c4b5fd', fontWeight: 700 }} />
        {newIds && newIds.size > 0 && (
          <Chip size="small" label={`🆕 нових: ${newIds.size}`}
            sx={{ bgcolor: 'rgba(255,45,45,0.25)', color: '#ff8a80', fontWeight: 700 }} />
        )}
        <Box flex={1} />
        {[['all','Всі'],['learning','🔥 Вчаться'],['active','✅ Є доступ'],['expired','⛔ Без доступу'],['quiet','😴 Тихі']].map(([k, lbl]) =>
          <Chip key={k} label={lbl} onClick={() => setFilter(k)}
            variant={filter === k ? 'filled' : 'outlined'}
            sx={{ bgcolor: filter === k ? 'var(--ac1, #7c4dff)' : 'transparent',
                  color: filter === k ? '#fff' : '#aaa', borderColor: '#555' }} />)}
        <TextField size="small" placeholder="🔍 username / tg id" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ width: 200 }} />
        <Button size="small" variant="contained"
          onClick={() => { setDmUser(null); setMsgOpen(true); }}
          sx={{ bgcolor: 'var(--ac1, #7c4dff)', '&:hover': { bgcolor: '#6a3de8' }, whiteSpace: 'nowrap' }}>
          📢 Всім ({reachable})
        </Button>
        {/* D-256: розіслати+закріпити вітання існуючим (welcome йде лише на 1-й /start) */}
        <Button size="small" variant="outlined"
          onClick={async () => {
            if (!window.confirm(`Розіслати + ЗАКРІПИТИ вітання ${reachable} досяжним юзерам? Кожен отримає вітальне повідомлення (запускати один раз).`)) return;
            try {
              const r = await jsonFetch(`${API_BASE}/pin-welcome-all`, { method: 'POST', body: '{}' });
              alert(`📌 Готово: надіслано+закріплено ${r.sent}/${r.total}`);
            } catch (e) { alert(e.message); }
          }}
          sx={{ color: '#5eead4', borderColor: '#5eead4', whiteSpace: 'nowrap' }}>
          📌 Закріпити вітання
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Користувач','Статус','Доступ до','Джерело','Мови','Слів / XP','Активність','Реєстрація','Дії'].map(h =>
                  <TableCell key={h} sx={{ color: '#aaa', fontWeight: 'bold' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 && (
                <TableRow><TableCell colSpan={9}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Немає юзерів</Typography>
                </TableCell></TableRow>
              )}
              {list.map(u => (
                <TableRow key={u.user_id} hover onClick={() => setCard(u)}
                  sx={{ cursor: 'pointer',
                        ...(newIds && newIds.has(u.user_id) ? { bgcolor: 'rgba(255,45,45,0.13)' } : {}) }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                      {u.username ? `@${u.username}` : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>tg:{u.telegram_id}</Typography>
                    {/* 💎 2026-09-11: новий і ще не прочитаний */}
                    {newIds && newIds.has(u.user_id) && (
                      <Chip size="small" label="🆕 новий"
                        sx={{ mt: 0.4, height: 18, fontSize: '0.65rem',
                              bgcolor: 'rgba(255,45,45,0.25)', color: '#ff8a80', display: 'flex' }} />
                    )}
                    {/* 💎 2026-08-13: «тихий» — зайшов і зник, інакше губиться у списку */}
                    {u.quiet && (
                      <Tooltip title={`Зайшов і зник: вивчив ${u.srs_words ?? 0} слів і не повернувся`}>
                        <Chip size="small" label="😴 зайшов і зник"
                          sx={{ ml: 0, mt: 0.4, height: 18, fontSize: '0.65rem',
                                bgcolor: 'rgba(251,146,60,0.18)', color: '#fdba74', display: 'flex' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#bbb' }}>{u.status || '—'}</Typography>
                  </TableCell>
                  <TableCell>{accessCell(u)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#bbb' }}>
                      {PL_SOURCE_LABEL[u.source] || u.source || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#bbb' }}>
                      {(u.native_lang || '?')}→{(u.learn_lang || '?')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#fff' }}>
                      {u.words_learned} / {u.xp_total}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#888' }}>{fmtDateShort(u.last_activity)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: '#888' }}>{fmtDateShort(u.created_at)}</Typography>
                  </TableCell>
                  {/* дії в рядку лишаються швидким шляхом — клік по них НЕ має
                      відкривати картку, інакше кожне натискання давало б діалог */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title={u.reachable ? 'Написати повідомлення' : 'Недосяжний — не активний у @PlayLanguage_bot'}>
                        <span>
                          <IconButton size="small" disabled={!u.reachable}
                            onClick={() => { setDmUser(u); setMsgOpen(true); }}
                            sx={{ color: u.reachable ? '#5eead4' : '#555' }}>✉️</IconButton>
                        </span>
                      </Tooltip>
                      <UserActions u={u} onUpdated={patchUser} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" sx={{ color: '#666' }}>
        Всього: {list.length} · 📢 досяжно для розсилки: {reachable}
      </Typography>
      <MessageDialog open={msgOpen} dmUser={dmUser} reachableCount={reachable}
        onClose={() => setMsgOpen(false)} />
      {card && (
        <PlUserCard u={card} onClose={() => setCard(null)}
          onUpdated={(brief) => { patchUser(brief); setCard(c => (c ? { ...c, ...brief } : c)); }}
          onMessage={(usr) => { setCard(null); setDmUser(usr); setMsgOpen(true); }} />
      )}
    </Stack>
  );
}


// ─── Words Pool Tab (L.2b) ──────────────────────────────────────────────────
const WORD_LANGS = ['uk', 'ru', 'de', 'fr', 'it', 'es', 'pl'];
const WORD_LANG_LABELS = { en: 'EN', uk: 'UA', ru: 'RU', de: 'DE', fr: 'FR', it: 'IT', es: 'ES', pl: 'PL' };
const WORD_LANG_FLAGS = { en:'🇬🇧', uk:'🇺🇦', ru:'🇷🇺', de:'🇩🇪', fr:'🇫🇷', it:'🇮🇹', es:'🇪🇸', pl:'🇵🇱' };
const EMPTY_WORD_FORM = { word: '', category: '', difficulty: 1, enabled: true, imageUrl: '', wordType: 'word' };
const DIFF_CHIP_COLOR = { 1: 'success', 2: 'info', 3: 'default', 4: 'warning', 5: 'error' };
const CATEGORY_EMOJI = { food:'🍎', travel:'✈️', nature:'🌿', animals:'🐾', tech:'💻', home:'🏠', body:'🧠', emotions:'💭', work:'💼', sports:'⚽', music:'🎵', art:'🎨' };
const KNOWN_CATEGORIES = Object.keys(CATEGORY_EMOJI);

function WordsTab() {
  const [words, setWords] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [cats, setCats] = React.useState([]);                  // D-245: динамічні категорії з БД
  const [typeFilter, setTypeFilter] = React.useState('');      // '' | 'word' | 'phrase'
  const [diffFilter, setDiffFilter] = React.useState('');      // '' | 1 | 2 | 3
  const [typeMenuAnchor, setTypeMenuAnchor] = React.useState(null);
  const [typeMenuFor, setTypeMenuFor] = React.useState('');
  const [err, setErr] = React.useState(null);

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editWord, setEditWord] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_WORD_FORM);
  const [autoTrans, setAutoTrans] = React.useState({});
  const [translating, setTranslating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [generatingImage, setGeneratingImage] = React.useState(false);
  const [refreshingPool, setRefreshingPool] = React.useState(false); // D-239
  const [imgHist, setImgHist] = React.useState({}); // D-240: історія фото per word → ← повернення
  const [imgBackBusy, setImgBackBusy] = React.useState(false);
  const [batchImgLoading, setBatchImgLoading] = React.useState(false);
  const [pexelsKeyDlg, setPexelsKeyDlg] = React.useState(false);
  const [pexelsKeyInput, setPexelsKeyInput] = React.useState('');
  const [pexelsKeySaving, setPexelsKeySaving] = React.useState(false);
  const [batchImgDone, setBatchImgDone] = React.useState(null);

  // Generate dialog
  const [genOpen, setGenOpen] = React.useState(false);
  const [genForm, setGenForm] = React.useState({ topic: '', count: 10, category: '', wordType: 'word', difficulty: 1 });
  const [generating, setGenerating] = React.useState(false);
  const [genResult, setGenResult] = React.useState(null);

  // Delete
  const [delConfirm, setDelConfirm] = React.useState(null);
  const [previewImage, setPreviewImage] = React.useState(null);

  const auth = { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` };

  const load = React.useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 500 });
    if (search) params.set('search', search);
    if (categoryFilter) params.set('category', categoryFilter);
    if (typeFilter) params.set('word_type', typeFilter);
    if (diffFilter) params.set('difficulty', diffFilter);
    fetch(`${API_BASE}/words?${params}`, { headers: auth })
      .then(r => r.json())
      .then(d => { setWords(d.items || []); setTotal(d.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
    // D-245: динамічні чіпи категорій (включно з кастомними з генерації)
    fetch(`${API_BASE}/words-categories`, { headers: auth })
      .then(r => r.json())
      .then(d => setCats(d.categories || []))
      .catch(() => {});
  }, [search, categoryFilter, typeFilter, diffFilter]);

  React.useEffect(() => { load(); }, [load]);

  // 💎 Q-PL-009: admin toggle — lock a word's photo (hides the in-app «Фото не те» button)
  const toggleLock = (w) => {
    fetch(`${API_BASE}/words/${w.id}`, {
      method: 'PUT',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_locked: !w.image_locked }),
    })
      .then(r => (r.ok ? r.json() : r.json().then(e => Promise.reject(e))))
      .then(() => load())
      .catch(() => setErr('Не вдалося змінити фіксацію фото'));
  };

  const openAdd = () => {
    setEditWord(null); setForm(EMPTY_WORD_FORM); setAutoTrans({}); setErr(null); setDialogOpen(true);
  };
  const openEdit = w => {
    setEditWord(w);
    const t = w.translations || {};
    setAutoTrans(t);
    setForm({ word: t.uk || w.word_en || '', category: w.category || '', difficulty: w.difficulty || 1, enabled: w.enabled !== false, imageUrl: w.image_url || '', wordType: w.word_type || 'word' });
    setErr(null); setDialogOpen(true);
  };

  const handleAutoTranslate = () => {
    if (!form.word.trim()) return;
    setTranslating(true);
    fetch(`${API_BASE}/words/auto-translate`, {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ word: form.word.trim() }),
    })
      .then(r => r.json())
      .then(d => { setAutoTrans(d.translations || {}); setTranslating(false); })
      .catch(() => setTranslating(false));
  };

  const handleImageUpload = (file) => {
    if (!file || !editWord) return;
    setUploadingImage(true);
    const fd = new FormData();
    fd.append('file', file);
    fetch(`${API_BASE}/words/${editWord.id}/upload-image`, {
      method: 'POST',
      headers: auth,
      body: fd,
    })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
      .then(d => { setForm(f => ({ ...f, imageUrl: d.image_url })); setUploadingImage(false); })
      .catch(() => setUploadingImage(false));
  };

  // 💎 Q-PL-009: generate a fresh associative photo (same Pexels engine as batch gen)
  const handleGenerateImage = () => {
    if (!editWord || generatingImage) return;
    setGeneratingImage(true);
    const prevUrl = form.imageUrl || null; // D-240: запам'ятати для ← повернення
    fetch(`${API_BASE}/words/${editWord.id}/generate-image`, { method: 'POST', headers: auth })
      .then(r => (r.ok ? r.json() : r.json().then(e => Promise.reject(e))))
      .then(d => {
        if (prevUrl) setImgHist(h => ({ ...h, [editWord.id]: [...(h[editWord.id] || []), prevUrl] }));
        setForm(f => ({ ...f, imageUrl: d.image_url })); setGeneratingImage(false); load();
      })
      .catch((e) => { setErr((e && e.detail) || 'Не вдалося згенерувати фото'); setGeneratingImage(false); });
  };

  // 💎 D-240: ← повернути попереднє фото (буває попередня асоціація була кращою)
  const handleImageBack = async () => {
    if (!editWord || imgBackBusy) return;
    const stack = imgHist[editWord.id] || [];
    if (!stack.length) return;
    const prev = stack[stack.length - 1];
    setImgBackBusy(true);
    try {
      await jsonFetch(`${API_BASE}/words/${editWord.id}`,
        { method: 'PUT', body: JSON.stringify({ image_url: prev }) });
      setImgHist(h => ({ ...h, [editWord.id]: stack.slice(0, -1) }));
      setForm(f => ({ ...f, imageUrl: prev }));
      load();
    } catch (e) { setErr((e && e.message) || 'Не вдалося повернути фото'); }
    setImgBackBusy(false);
  };

  // 💎 D-239: пересіяти пул фото-альтернатив гри (для «жодне не підходить»-слів)
  const handlePoolRefresh = () => {
    if (!editWord || refreshingPool) return;
    setRefreshingPool(true);
    fetch(`${API_BASE}/words/${editWord.id}/photo-pool-refresh`, { method: 'POST', headers: auth })
      .then(r => (r.ok ? r.json() : r.json().then(e => Promise.reject(e))))
      .then(d => {
        setRefreshingPool(false);
        alert(`✅ Пул пересіяно (${(d.pool || []).length} фото, запит: "${d.query_used}")` +
              (d.reports_resolved ? `\n📋 Закрито скарг: ${d.reports_resolved}` : ''));
        load();
      })
      .catch((e) => { setErr((e && e.detail) || 'Не вдалося пересіяти пул'); setRefreshingPool(false); });
  };

  const handleBatchImages = () => {
    setBatchImgLoading(true);
    setBatchImgDone(null);
    fetch(`${API_BASE}/words/batch-generate-images`, { method: 'POST', headers: auth })
      .then(r => r.json())
      .then(d => {
        setBatchImgLoading(false);
        if (d.no_key) { setPexelsKeyDlg(true); return; }
        setBatchImgDone('✅ Запущено! Фото з\'являться через 1-2 хв.');
        setTimeout(() => { setBatchImgDone(null); load(); }, 10000);
      })
      .catch(() => { setBatchImgLoading(false); setBatchImgDone('❌ Помилка'); });
  };

  const handleSavePexelsKey = () => {
    if (!pexelsKeyInput.trim()) return;
    setPexelsKeySaving(true);
    fetch(`/api/api-keys/pexels?key=${encodeURIComponent(pexelsKeyInput.trim())}`, { method: 'POST', headers: auth })
      .then(() => { setPexelsKeySaving(false); setPexelsKeyDlg(false); setPexelsKeyInput(''); handleBatchImages(); })
      .catch(() => { setPexelsKeySaving(false); setBatchImgDone('❌ Помилка збереження ключа'); });
  };

  const save = () => {
    setSaving(true); setErr(null);
    if (editWord) {
      // Edit: use existing PUT endpoint with full translations
      const body = {
        word_en: autoTrans.en || editWord.word_en,
        translations: autoTrans,
        category: form.category || null,
        difficulty: parseInt(form.difficulty, 10) || 1,
        enabled: form.enabled,
        word_type: form.wordType || 'word',
        ...(form.imageUrl !== (editWord.image_url || '') ? { image_url: form.imageUrl || null } : {}),
      };
      fetch(`${API_BASE}/words/${editWord.id}`, {
        method: 'PUT', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
        .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
        .then(() => { setDialogOpen(false); load(); setSaving(false); })
        .catch(e => { setErr(e?.detail || 'Помилка збереження'); setSaving(false); });
    } else {
      // Create: use smart endpoint with auto-translate + auto-Unsplash
      const body = {
        word_en: autoTrans.en || form.word,
        translations: Object.keys(autoTrans).length > 0 ? autoTrans : { uk: form.word },
        category: form.category || null,
        difficulty: parseInt(form.difficulty, 10) || 1,
        enabled: form.enabled,
        word_type: form.wordType || 'word',
      };
      fetch(`${API_BASE}/words/create-smart`, {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
        .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
        .then(() => { setDialogOpen(false); load(); setSaving(false); })
        .catch(e => { setErr(e?.detail || 'Помилка збереження'); setSaving(false); });
    }
  };

  const doDelete = () => {
    if (!delConfirm) return;
    fetch(`${API_BASE}/words/${delConfirm.id}`, { method: 'DELETE', headers: auth })
      .then(() => { setDelConfirm(null); load(); });
  };

  const handleGenerate = () => {
    if (!genForm.topic.trim()) return;
    setGenerating(true); setGenResult(null);
    fetch(`${API_BASE}/words/generate`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ topic: genForm.topic, count: genForm.count, category: genForm.category || null, word_type: genForm.wordType || 'word', difficulty: genForm.difficulty || 1 }),
    })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
      .then(d => { setGenResult(d); setGenerating(false); load(); })
      .catch(e => { setErr(e?.detail || 'Помилка генерації'); setGenerating(false); });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ color: '#fff' }}>📚 Words Pool ({total})</Typography>
        <Stack direction="row" spacing={1}>
          <TextField size="small" placeholder="Пошук..." value={search}
            onChange={e => setSearch(e.target.value)} sx={{ ...INPUT_SX, width: 180 }} />
          <Button variant="outlined" onClick={() => { setGenOpen(true); setGenResult(null); setErr(null); }}
            sx={{ color: 'var(--ac2, #a78bfa)', borderColor: '#7c3aed', '&:hover': { borderColor: 'var(--ac2, #a78bfa)', bgcolor: 'rgba(124,58,237,0.1)' } }}>
            🤖 Генерувати
          </Button>
          <Button variant="contained" size="small" disabled={batchImgLoading}
            onClick={handleBatchImages}
            sx={{ bgcolor: '#0f766e', color: '#fff', '&:hover': { bgcolor: '#0d9488' }, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
            {batchImgLoading ? '⏳ Шукаємо фото...' : '🖼 Pexels фото'}
          </Button>
          {batchImgDone && (
            <Typography variant="caption" sx={{ color: batchImgDone.startsWith('✅') ? '#4ade80' : '#f87171', maxWidth: 160 }}>
              {batchImgDone}
            </Typography>
          )}
          <Button variant="contained" onClick={openAdd} sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
            + Додати
          </Button>
        </Stack>
      </Stack>

      {/* Type / Level filter */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" mb={1} useFlexGap alignItems="center">
        <Chip label="Всі" size="small"
          onClick={() => { setTypeFilter(''); setDiffFilter(''); }}
          sx={{ bgcolor: !typeFilter ? '#7c3aed' : '#2a2a4a', color: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#6d28d9' } }} />
        {[['word', '📖 Слова'], ['phrase', '💬 Фрази']].map(([tp, label]) => {
          const active = typeFilter === tp;
          const diffLabel = active && diffFilter
            ? (tp === 'phrase'
                ? [' · ★', ' · ★★', ' · ★★★'][diffFilter - 1]
                : [' · L1', ' · L2', ' · L3'][diffFilter - 1])
            : '';
          return (
            <Chip key={tp}
              label={`${label}${diffLabel} ▾`}
              size="small"
              onClick={e => { setTypeMenuAnchor(e.currentTarget); setTypeMenuFor(tp); }}
              sx={{
                bgcolor: active ? (tp === 'phrase' ? '#b45309' : '#5b21b6') : '#2a2a4a',
                color: '#fff', cursor: 'pointer', fontSize: '0.78rem',
                '&:hover': { bgcolor: active ? (tp === 'phrase' ? '#92400e' : '#4c1d95') : '#3a3a5a' },
              }} />
          );
        })}
        <Typography variant="caption" sx={{ color: '#555', ml: 0.5 }}>│</Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {total} {typeFilter === 'phrase' ? 'фраз' : typeFilter === 'word' ? 'слів' : 'всього'}
          {diffFilter ? ` · рівень ${diffFilter}` : ''}
        </Typography>
      </Stack>

      {/* Type dropdown Menu */}
      <Menu anchorEl={typeMenuAnchor} open={!!typeMenuAnchor} onClose={() => setTypeMenuAnchor(null)}
        PaperProps={{ sx: { bgcolor: '#1a0a2e', color: '#fff', border: '1px solid #555', minWidth: 200 } }}>
        <MenuItem dense
          onClick={() => { setTypeFilter(typeMenuFor); setDiffFilter(''); setTypeMenuAnchor(null); }}
          sx={{ color: typeFilter === typeMenuFor && !diffFilter ? 'var(--ac2, #a78bfa)' : '#eee',
                '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.20)' }, fontSize: '0.85rem' }}>
          {typeMenuFor === 'phrase' ? '💬 Всі фрази' : '📖 Всі слова'}
        </MenuItem>
        <Divider sx={{ borderColor: '#333', my: 0.5 }} />
        {(typeMenuFor === 'phrase'
          ? [{d:1, label:'★ Фрази — Початківець'}, {d:2, label:'★★ Фрази — Середній'}, {d:3, label:'★★★ Фрази — Просунутий'}]
          : [{d:1, label:'⭐ Слова — Початківець'}, {d:2, label:'⭐⭐ Слова — Середній'}, {d:3, label:'⭐⭐⭐ Слова — Просунутий'}]
        ).map(({ d, label }) => (
          <MenuItem key={d} dense
            onClick={() => { setTypeFilter(typeMenuFor); setDiffFilter(d); setTypeMenuAnchor(null); }}
            sx={{ color: typeFilter === typeMenuFor && diffFilter === d ? 'var(--ac2, #a78bfa)' : '#eee',
                  '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.20)' }, fontSize: '0.85rem' }}>
            {label}
          </MenuItem>
        ))}
      </Menu>

      {/* Category filter chips */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" mb={1.5} useFlexGap>
        <Chip label="Всі категорії" size="small"
          onClick={() => setCategoryFilter('')}
          sx={{ bgcolor: !categoryFilter ? '#1e3a5f' : '#1a1a2e', color: !categoryFilter ? '#60a5fa' : '#666',
                border: '1px solid', borderColor: !categoryFilter ? '#3b82f6' : '#333',
                cursor: 'pointer', fontSize: '0.72rem', '&:hover': { bgcolor: '#1e3a5f' } }} />
        {/* D-245: категорії з БД (кастомні з'являються одразу після генерації), не хардкод */}
        {(cats.length ? cats.map(c => c.name) : KNOWN_CATEGORIES).map(cat => (
          <Chip key={cat}
            label={`${CATEGORY_EMOJI[cat] || '🏷️'} ${cat}${(() => { const f = cats.find(x => x.name === cat); return f ? ` (${f.count})` : ''; })()}`}
            size="small"
            onClick={() => setCategoryFilter(c => c === cat ? '' : cat)}
            sx={{ bgcolor: categoryFilter === cat ? '#1e3a5f' : '#1a1a2e',
                  color: categoryFilter === cat ? '#60a5fa' : '#666',
                  border: '1px solid', borderColor: categoryFilter === cat ? '#3b82f6' : '#333',
                  cursor: 'pointer', fontSize: '0.72rem',
                  '&:hover': { bgcolor: '#1e3a5f' } }} />
        ))}
      </Stack>

      {/* D-241: спінер лише на ПЕРШЕ завантаження — при reload таблиця лишається змонтованою,
          інакше TableContainer демонтується і скрол злітає на початок після кожного збереження */}
      {loading && words.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#7c3aed' }} /></Box>
      ) : words.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
          <Typography variant="h4">📭</Typography>
          <Typography>Слів ще немає. Натисніть "🤖 Генерувати" або "+ Додати"</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa', fontWeight: 700 }}>🇬🇧 EN (ключ)</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>🇺🇦 UK</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>Тип</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>Категорія</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>Складн.</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>Увімк.</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>Мови</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>Фото</TableCell>
                <TableCell sx={{ bgcolor: '#1a1a2e', color: '#aaa' }}>Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {words.map(w => {
                const t = w.translations || {};
                const langsDone = WORD_LANGS.filter(l => t[l]).length;
                return (
                  <TableRow key={w.id} hover sx={{ '&:hover': { bgcolor: '#1e1e3a' } }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{w.word_en}</TableCell>
                    <TableCell sx={{ color: '#ccc', fontSize: '0.82rem' }}>{t.uk || '—'}</TableCell>
                    <TableCell>
                      <Chip label={w.word_type === 'phrase' ? '💬 Фраза' : '📖 Слово'} size="small"
                        sx={{ bgcolor: w.word_type === 'phrase' ? 'rgba(251,191,36,0.15)' : 'rgba(124,58,237,0.15)',
                          color: w.word_type === 'phrase' ? '#fbbf24' : 'var(--ac2, #a78bfa)', fontSize: '0.72rem',
                          border: '1px solid', borderColor: w.word_type === 'phrase' ? '#fbbf2466' : 'var(--ac2, #a78bfa)66' }} />
                    </TableCell>
                    <TableCell sx={{ color: '#aaa', fontSize: '0.82rem' }}>{w.category || '—'}</TableCell>
                    <TableCell><Chip label={w.difficulty} size="small" color={DIFF_CHIP_COLOR[w.difficulty] || 'default'} /></TableCell>
                    <TableCell><Chip label={w.enabled ? 'ON' : 'OFF'} size="small" color={w.enabled ? 'success' : 'default'} /></TableCell>
                    <TableCell>
                      <Chip label={`${langsDone}/${WORD_LANGS.length}`} size="small"
                        color={langsDone === WORD_LANGS.length ? 'success' : langsDone > 3 ? 'info' : 'warning'} />
                    </TableCell>
                    <TableCell>
                      {w.image_url ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <img src={w.image_url} alt={w.word_en} style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                            onClick={() => setPreviewImage(w.image_url)} />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {w.image_url?.includes('pexels.com') && (
                              <Tooltip title="Photos provided by Pexels">
                                <Box component="a" href="https://www.pexels.com" target="_blank" rel="noopener noreferrer"
                                  sx={{ fontSize: '0.58rem', color: '#05a081', fontWeight: 700, lineHeight: 1,
                                    textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Pexels</Box>
                              </Tooltip>
                            )}
                            {w.image_flagged && (
                              <Tooltip title="Юзер повідомив: фото не відповідає слову">
                                <span style={{ fontSize: 14, cursor: 'help' }}>⚠️</span>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Tooltip title={`Категорія: ${w.category || '—'} (фото не додано)`}>
                          <Box sx={{ width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'rgba(124,58,237,0.12)', borderRadius: 4, fontSize: 20, cursor: 'pointer' }}
                            onClick={() => openEdit(w)}>
                            {CATEGORY_EMOJI[w.category?.toLowerCase()] || '📚'}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={w.image_locked ? '🔒 Фото зафіксовано — кнопку «Фото не те» приховано в застосунку. Натисніть, щоб повернути' : 'Зафіксувати фото — прибрати кнопку «Фото не те» у застосунку'}>
                          <IconButton size="small" onClick={() => toggleLock(w)}
                            sx={w.image_locked
                              ? { color: '#22c55e', bgcolor: 'rgba(34,197,94,0.18)', border: '1px solid #22c55e', borderRadius: 1, '&:hover': { bgcolor: 'rgba(34,197,94,0.32)' } }
                              : { color: '#777' }}>
                            {w.image_locked ? '🔒' : '🔓'}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Редагувати"><IconButton size="small" onClick={() => openEdit(w)} sx={{ color: '#7c3aed' }}>✏️</IconButton></Tooltip>
                        <Tooltip title="Видалити"><IconButton size="small" onClick={() => setDelConfirm(w)} sx={{ color: '#f44336' }}>🗑</IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {err && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setErr(null)}>{err}</Alert>}

      {/* ── Add/Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e1e2e', color: '#fff' } }}>
        <DialogTitle sx={{ color: '#fff' }}>{editWord ? '✏️ Редагувати слово' : '➕ Нове слово'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {!editWord ? (
              <>
                <TextField
                  label="Слово (українською або будь-якою мовою)"
                  value={form.word}
                  onChange={e => { setForm(f => ({ ...f, word: e.target.value })); setAutoTrans({}); }}
                  fullWidth sx={INPUT_SX}
                  helperText="Введіть слово → натисніть 🌐 Перекласти — AI заповнить всі 8 мов"
                />
                <Button
                  variant="outlined" onClick={handleAutoTranslate}
                  disabled={!form.word.trim() || translating}
                  sx={{ color: 'var(--ac2, #a78bfa)', borderColor: '#7c3aed' }}
                >
                  {translating ? '⏳ Перекладаю...' : '🌐 Перекласти на всі 8 мов'}
                </Button>
                {Object.keys(autoTrans).length > 0 && (
                  <Box sx={{ bgcolor: '#161630', borderRadius: 2, p: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#aaa', mb: 1, display: 'block' }}>Переклади (авто):</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {Object.entries(autoTrans).map(([lang, val]) => (
                        <Chip key={lang} label={`${WORD_LANG_FLAGS[lang] || lang} ${val}`} size="small"
                          sx={{ bgcolor: '#2a2a4a', color: '#c4b5fd', fontSize: '0.78rem' }} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: '#aaa' }}>Переклади (редагувати):</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ bgcolor: '#161630', borderRadius: 2, p: 1.5 }}>
                  {['uk','ru','en','de','fr','it','es','pl'].map(lang => (
                    <TextField key={lang}
                      label={`${WORD_LANG_FLAGS[lang]} ${WORD_LANG_LABELS[lang]}`}
                      value={autoTrans[lang] || ''}
                      onChange={e => setAutoTrans(t => ({ ...t, [lang]: e.target.value }))}
                      size="small" sx={{ ...INPUT_SX, width: 140 }} />
                  ))}
                </Stack>
                <Box sx={{ bgcolor: '#161630', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {form.imageUrl ? (
                    <img src={form.imageUrl} alt="word" title="Натисніть, щоб збільшити"
                      onClick={() => setPreviewImage(form.imageUrl)}
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, flexShrink: 0, cursor: 'zoom-in' }} />
                  ) : (
                    <Box sx={{ width: 72, height: 72, bgcolor: '#2a2a4a', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>🖼</Box>
                  )}
                  <Box>
                    <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 0.5 }}>
                      {form.imageUrl ? 'Поточне фото (можна замінити)' : 'Фото відсутнє'}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button component="label" variant="outlined" size="small" disabled={uploadingImage}
                        sx={{ color: 'var(--ac2, #a78bfa)', borderColor: '#7c3aed', fontSize: '0.78rem' }}>
                        {uploadingImage ? '⏳ Завантаження...' : '📷 ' + (form.imageUrl ? 'Замінити фото' : 'Завантажити фото')}
                        <input type="file" hidden accept="image/jpeg,image/png,image/webp"
                          onChange={e => { if (e.target.files[0]) handleImageUpload(e.target.files[0]); e.target.value = ''; }} />
                      </Button>
                      {editWord && (imgHist[editWord.id] || []).length > 0 && (
                        <Tooltip title="← Повернути попередню фотографію (буває попередня асоціація була кращою)">
                          <Button variant="outlined" size="small" disabled={imgBackBusy} onClick={handleImageBack}
                            sx={{ color: '#fbbf24', borderColor: '#b45309', fontSize: '0.78rem', minWidth: 42 }}>
                            {imgBackBusy ? '⏳' : '←'}
                          </Button>
                        </Tooltip>
                      )}
                      {editWord && (
                        <Button variant="outlined" size="small" disabled={generatingImage} onClick={handleGenerateImage}
                          sx={{ color: '#34d399', borderColor: '#059669', fontSize: '0.78rem' }}>
                          {generatingImage ? '⏳ Генерую...' : '🎲 Генерувати фото'}
                        </Button>
                      )}
                      {editWord && (
                        <Tooltip title="Пересіяти 5 альтернатив для Гри в асоціації (пошук word_en + категорія), скинути голоси, повернути слово в гру, закрити game_none-скаргу">
                          <Button variant="outlined" size="small" disabled={refreshingPool} onClick={handlePoolRefresh}
                            sx={{ color: '#5eead4', borderColor: '#0d9488', fontSize: '0.78rem' }}>
                            {refreshingPool ? '⏳ Пересіюю...' : '🔄 Пул фото (гра)'}
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </>
            )}
            <Divider sx={{ borderColor: '#333' }} />
            <Stack direction="row" spacing={2}>
              <TextField label="Категорія" value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                fullWidth sx={INPUT_SX} placeholder="food, travel, verbs..." />
              <TextField label="Складність (1-5)" type="number" value={form.difficulty}
                onChange={e => setForm(f => ({ ...f, difficulty: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)) }))}
                inputProps={{ min: 1, max: 5 }} sx={{ ...INPUT_SX, width: 140 }} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ color: '#aaa', fontSize: '0.9rem' }}>Тип:</Typography>
              {['word', 'phrase'].map(tp => (
                <Button key={tp} size="small" variant={form.wordType === tp ? 'contained' : 'outlined'}
                  onClick={() => setForm(f => ({ ...f, wordType: tp }))}
                  sx={form.wordType === tp
                    ? { bgcolor: tp === 'phrase' ? '#b45309' : '#7c3aed', color: '#fff', fontSize: '0.82rem' }
                    : { color: tp === 'phrase' ? '#fbbf24' : 'var(--ac2, #a78bfa)',
                        borderColor: tp === 'phrase' ? '#fbbf24' : '#7c3aed', fontSize: '0.82rem' }}>
                  {tp === 'phrase' ? '💬 Фраза' : '📖 Слово'}
                </Button>
              ))}
            </Stack>
            <FormControlLabel
              label={<Typography sx={{ color: '#fff' }}>Увімкнено</Typography>}
              control={<Switch checked={!!form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                sx={{ '& .MuiSwitch-thumb': { bgcolor: form.enabled ? '#7c3aed' : '#666' } }} />}
            />
            {err && <Alert severity="error">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#aaa' }}>Скасувати</Button>
          <Button onClick={save}
            disabled={saving || (!editWord && !form.word.trim())}
            variant="contained" sx={{ bgcolor: '#7c3aed' }}>
            {saving ? '⏳' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Generate Dialog ── */}
      <Dialog open={genOpen} onClose={() => setGenOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e1e2e', color: '#fff' } }}>
        <DialogTitle sx={{ color: 'var(--ac2, #a78bfa)' }}>🤖 Генерувати слова через AI</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Тема / категорія (українською або англійською)"
              value={genForm.topic} onChange={e => setGenForm(f => ({ ...f, topic: e.target.value }))}
              fullWidth sx={INPUT_SX} placeholder="кухня, емоції, транспорт, дієслова дій..." />
            <Box>
              <Typography variant="caption" sx={{ color: '#888', mb: 0.75, display: 'block' }}>Швидкий вибір теми:</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {[
                  'Знайомство', 'Ввічливість', 'Вибачення', 'Прохання',
                  'Подорожі', 'Ресторан', 'Магазини', 'Готель',
                  'Здоров\'я', 'Лікарня', 'Аптека', 'Спорт',
                  'Погода', 'Час і дати', 'Гроші', 'Банк',
                  'Робота', 'Навчання', 'Технології', 'Соцмережі',
                  'Сім\'я', 'Дружба', 'Стосунки', 'Дім і побут',
                  'Природа', 'Тварини', 'Мистецтво', 'Музика',
                  'Їжа і кухня', 'Транспорт', 'Емоції', 'Надзвичайна ситуація',
                ].map(t => (
                  <Chip key={t} label={t} size="small" onClick={() => setGenForm(f => ({ ...f, topic: t }))}
                    sx={{ cursor: 'pointer', mb: 0.5,
                      bgcolor: genForm.topic === t ? '#7c3aed' : '#2a2a4a',
                      color: genForm.topic === t ? '#fff' : '#c4b5fd',
                      border: '1px solid', borderColor: genForm.topic === t ? '#7c3aed' : '#4a4a6a',
                      '&:hover': { bgcolor: '#3a3a5a' }, fontSize: '0.75rem' }} />
                ))}
              </Stack>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography sx={{ color: '#aaa', whiteSpace: 'nowrap' }}>Кількість: {genForm.count}</Typography>
              <input type="range" min={5} max={30} value={genForm.count}
                onChange={e => setGenForm(f => ({ ...f, count: parseInt(e.target.value) }))}
                style={{ flex: 1, accentColor: '#7c3aed' }} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ color: '#aaa', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>Тип:</Typography>
              {['word', 'phrase'].map(tp => (
                <Button key={tp} size="small" variant={genForm.wordType === tp ? 'contained' : 'outlined'}
                  onClick={() => setGenForm(f => ({ ...f, wordType: tp, difficulty: 1 }))}
                  sx={genForm.wordType === tp
                    ? { bgcolor: tp === 'phrase' ? '#b45309' : '#7c3aed', color: '#fff', fontSize: '0.82rem' }
                    : { color: tp === 'phrase' ? '#fbbf24' : 'var(--ac2, #a78bfa)',
                        borderColor: tp === 'phrase' ? '#fbbf24' : '#7c3aed', fontSize: '0.82rem' }}>
                  {tp === 'phrase' ? '💬 Фрази' : '📖 Слова'}
                </Button>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography sx={{ color: '#aaa', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>Рівень:</Typography>
              {(genForm.wordType === 'phrase'
                ? [{d:1,label:'💬 Фрази — Початківець'},{d:2,label:'💬 Фрази — Середній'},{d:3,label:'💬 Фрази — Просунутий'}]
                : [{d:1,label:'⭐ Початківець'},{d:2,label:'⭐⭐ Середній'},{d:3,label:'⭐⭐⭐ Просунутий'}]
              ).map(({d, label}) => (
                <Button key={d} size="small" variant={genForm.difficulty === d ? 'contained' : 'outlined'}
                  onClick={() => setGenForm(f => ({ ...f, difficulty: d }))}
                  sx={genForm.difficulty === d
                    ? { bgcolor: '#0f766e', color: '#fff', fontSize: '0.78rem' }
                    : { color: '#5eead4', borderColor: '#0f766e', fontSize: '0.78rem' }}>
                  {label}
                </Button>
              ))}
            </Stack>
            <TextField label="Категорія для БД (опц.)"
              value={genForm.category} onChange={e => setGenForm(f => ({ ...f, category: e.target.value }))}
              fullWidth sx={INPUT_SX} placeholder="food, emotions, verbs..." />
            {genResult && (
              <Alert severity="success">
                ✅ Створено {genResult.created} слів! Переклади + картинки додано автоматично.
              </Alert>
            )}
            {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
            <Typography variant="caption" sx={{ color: '#666' }}>
              AI генерує слова → Google Translate перекладає на 8 мов → Unsplash підтягує картинки (якщо ключ налаштований).
              Потрібен API key: anthropic або openai у Dashboard → Безпека.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenOpen(false)} sx={{ color: '#aaa' }}>Закрити</Button>
          <Button onClick={handleGenerate}
            disabled={!genForm.topic.trim() || generating}
            variant="contained" sx={{ bgcolor: '#7c3aed' }}>
            {generating ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />Генерую...</> : '🤖 Генерувати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirm ── */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="md"
        PaperProps={{ sx: { bgcolor: '#111', boxShadow: 'none', borderRadius: 3 }, onClick: () => setPreviewImage(null) }}>
        <Box sx={{ p: 1, cursor: 'zoom-out' }}>
          {previewImage && <img src={previewImage} alt="preview" style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', borderRadius: 8 }} />}
        </Box>
      </Dialog>

      <Dialog open={!!delConfirm} onClose={() => setDelConfirm(null)}
        PaperProps={{ sx: { bgcolor: '#1e1e2e', color: '#fff' } }}>
        <DialogTitle sx={{ color: '#f44336' }}>🗑 Видалити слово?</DialogTitle>
        <DialogContent>
          <Typography>Видалити <b style={{ color: '#7c3aed' }}>{delConfirm?.word_en}</b>?</Typography>
          <Typography variant="caption" sx={{ color: '#f44', mt: 1, display: 'block' }}>
            Прогрес всіх юзерів по цьому слову також буде видалено.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelConfirm(null)} sx={{ color: '#aaa' }}>Скасувати</Button>
          <Button onClick={doDelete} variant="contained" color="error">Видалити</Button>
        </DialogActions>
      </Dialog>

      {/* Pexels API Key Dialog */}
      <Dialog open={pexelsKeyDlg} onClose={() => { setPexelsKeyDlg(false); setBatchImgLoading(false); }} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid #7c3aed' } }}>
        <DialogTitle sx={{ color: '#fff' }}>🖼 Pexels API Key</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            Зареєструйтесь на{' '}
            <Box component="a" href="https://www.pexels.com/api/" target="_blank" rel="noopener noreferrer"
              sx={{ color: '#05a081' }}>pexels.com/api</Box>
            {' '}та скопіюйте безкоштовний ключ.
          </Typography>
          <TextField
            label="Pexels API Key" value={pexelsKeyInput}
            onChange={e => setPexelsKeyInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && pexelsKeyInput.trim()) handleSavePexelsKey(); }}
            fullWidth type="password" autoComplete="off"
            InputProps={{ style: { color: '#fff', fontFamily: 'monospace' } }}
            InputLabelProps={{ style: { color: '#aaa' } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPexelsKeyDlg(false); setBatchImgLoading(false); }} sx={{ color: '#aaa' }}>Скасувати</Button>
          <Button onClick={handleSavePexelsKey} variant="contained"
            disabled={!pexelsKeyInput.trim() || pexelsKeySaving}
            sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>
            {pexelsKeySaving ? '⏳ Зберігаємо...' : 'Зберегти та запустити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// =============================================================================
// 💎 PlayLanguage (Batch B #2): word/phrase error reports (pencil ✏️)
// Shows what users reported next to the exact word; resolve + per-user pencil off
// =============================================================================
function ReportsTab({ onCountChange }) {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('new');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/reports?status=${filter}`)
      .then(r => { setList(r.reports || []); if (onCountChange) onCountChange(); })
      .catch(e => alert(e.message));
  }, [filter, onCountChange]);
  useEffect(load, [load]);

  const resolve = async (id) => {
    const note = window.prompt('Коментар (опційно):', '') ?? '';
    setBusy(true);
    try {
      await jsonFetch(`${API_BASE}/reports/${id}/resolve`,
        { method: 'POST', body: JSON.stringify({ decision: 'reviewed', note }) });
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const setPencil = async (uid, enabled) => {
    if (!uid) { alert('Немає user_id'); return; }
    if (!enabled && !window.confirm('Вимкнути олівець ✏️ для цього юзера?')) return;
    setBusy(true);
    try {
      await jsonFetch(`${API_BASE}/users/${uid}/pencil`,
        { method: 'POST', body: JSON.stringify({ enabled }) });
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  // 💎 D-239: «жодне фото не підходить» → пересіяти пул гри прямо зі скарги (авто-resolve)
  const poolRefresh = async (r) => {
    if (!r.word_id) return;
    if (!window.confirm('Пересіяти пул фото для цього слова? Голоси гри скинуться, скарга закриється.')) return;
    setBusy(true);
    try {
      const d = await jsonFetch(`${API_BASE}/words/${r.word_id}/photo-pool-refresh`, { method: 'POST', body: '{}' });
      alert(`✅ Пул пересіяно (${(d.pool || []).length} фото, запит: "${d.query_used}")`);
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  // 💎 Q-PL-012 (OWNER 07.06.2026): редагування слова просто зі скарги
  const [editW, setEditW] = useState(null);   // { wordId, translations, en, uk, imageUrl }
  const [savingW, setSavingW] = useState(false);
  const [zoomR, setZoomR] = useState(null);    // D-242: лайтбокс фото у скаргах
  const [genBusyR, setGenBusyR] = useState(false);
  const [histR, setHistR] = useState([]);      // ← історія фото в діалозі
  const openEdit = async (r) => {
    try {
      const res = await jsonFetch(`${API_BASE}/words?word_id=${r.word_id}&limit=1`);
      const w = (res.items || [])[0];
      if (!w) { alert('Слово не знайдено'); return; }
      const tr = w.translations || {};
      setHistR([]);
      setEditW({ reportId: r.id, wordId: w.id, translations: tr, en: w.word_en || tr.en || '',
                 uk: tr.uk || w.word_ua || '', imageUrl: w.image_url || null });
    } catch (e) { alert(e.message); }
  };
  // 🎲 перегенерувати фото зі скарги (+ ← повернення до попереднього)
  const genR = async () => {
    if (!editW || genBusyR) return;
    setGenBusyR(true);
    const prev = editW.imageUrl || null;
    try {
      const d = await jsonFetch(`${API_BASE}/words/${editW.wordId}/generate-image`, { method: 'POST', body: '{}' });
      if (prev) setHistR(h => [...h, prev]);
      setEditW(p => ({ ...p, imageUrl: d.image_url }));
    } catch (e) { alert(e.message); } finally { setGenBusyR(false); }
  };
  const backR = async () => {
    if (!editW || genBusyR || !histR.length) return;
    const prev = histR[histR.length - 1];
    setGenBusyR(true);
    try {
      await jsonFetch(`${API_BASE}/words/${editW.wordId}`,
        { method: 'PUT', body: JSON.stringify({ image_url: prev }) });
      setHistR(h => h.slice(0, -1));
      setEditW(p => ({ ...p, imageUrl: prev }));
    } catch (e) { alert(e.message); } finally { setGenBusyR(false); }
  };
  // 📷 D-243: своє фото, коли Pexels нічого підходящого не дає
  const uploadR = async (file) => {
    if (!editW || genBusyR || !file) return;
    setGenBusyR(true);
    const prev = editW.imageUrl || null;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/words/${editW.wordId}/upload-image`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
      const d = await res.json();
      if (prev) setHistR(h => [...h, prev]);
      setEditW(p => ({ ...p, imageUrl: d.image_url }));
    } catch (e) { alert(e.message); } finally { setGenBusyR(false); }
  };
  const saveW = async (alsoResolve) => {
    if (!editW) return;
    setSavingW(true);
    try {
      const translations = { ...editW.translations, en: editW.en, uk: editW.uk };
      await jsonFetch(`${API_BASE}/words/${editW.wordId}`,
        { method: 'PUT', body: JSON.stringify({ word_en: editW.en, translations }) });
      if (alsoResolve && editW.reportId != null) {
        await jsonFetch(`${API_BASE}/reports/${editW.reportId}/resolve`,
          { method: 'POST', body: JSON.stringify({ decision: 'reviewed', note: 'Виправлено ✅' }) });
      }
      setEditW(null);
      load();
    } catch (e) { alert(e.message); } finally { setSavingW(false); }
  };

  return (
    <Stack spacing={3} sx={INPUT_SX}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ color: '#fff' }}>✏️ Скарги на слова</Typography>
        <Box flex={1} />
        {['new', 'resolved', 'all'].map(s =>
          <Chip key={s} label={s} onClick={() => setFilter(s)}
            variant={filter === s ? 'filled' : 'outlined'}
            sx={{ bgcolor: filter === s ? 'var(--ac1, #7c4dff)' : 'transparent',
                  color: filter === s ? '#fff' : '#aaa', borderColor: '#555' }} />)}
      </Stack>
      {list.length === 0 && <Alert severity="info">Немає скарг зі статусом "{filter}"</Alert>}
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['ID','Час','User','Слово / Переклад','Повідомлення','Статус','Actions'].map(c =>
                <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(r => (
              <TableRow key={r.id} sx={{ '& td': { color: '#fff', verticalAlign: 'top' } }}>
                <TableCell>#{r.id}</TableCell>
                <TableCell>{fmtDate(r.created_at)}</TableCell>
                <TableCell>
                  {r.username ? `@${r.username}` : '—'}
                  <br />
                  <span style={{ color: '#888', fontSize: '0.75rem' }}>
                    tg:{r.telegram_id} db:{r.user_id || '?'}
                  </span>
                  {r.pencil_hidden &&
                    <><br /><Chip size="small" label="✏️ off" color="warning" sx={{ mt: 0.5 }} /></>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#f0f0ff' }}>
                    {r.study || r.word_en || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--ac2, #a78bfa)' }}>
                    {r.translation || r.word_ua || ''}
                  </Typography>
                  {r.word_id != null &&
                    <><br /><span style={{ color: '#666', fontSize: '0.7rem' }}>word #{r.word_id}</span></>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#fca5a5', maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                    {r.user_msg}
                  </Typography>
                  {r.owner_note &&
                    <Typography variant="caption" sx={{ color: '#5eead4' }}>↳ {r.owner_note}</Typography>}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.status}
                    color={r.status === 'resolved' ? 'success' : 'warning'} />
                </TableCell>
                <TableCell align="right">
                  {r.word_id != null &&
                    <Button size="small" disabled={busy} onClick={() => openEdit(r)}
                            sx={{ color: 'var(--ac2, #a78bfa)' }}>✏️ Слово</Button>}
                  {r.word_id != null && (r.user_msg || '').includes('Гра в асоціації') && r.status !== 'resolved' &&
                    <Button size="small" disabled={busy} onClick={() => poolRefresh(r)}
                            sx={{ color: '#34d399' }}>🔄 Пул фото</Button>}
                  {r.status !== 'resolved' &&
                    <Button size="small" disabled={busy} onClick={() => resolve(r.id)}
                            sx={{ color: '#5eead4' }}>✓ Resolve</Button>}
                  {r.user_id && (r.pencil_hidden
                    ? <Button size="small" disabled={busy} onClick={() => setPencil(r.user_id, true)}
                              sx={{ color: '#5eead4' }}>✏️ Увімкнути</Button>
                    : <Button size="small" disabled={busy} onClick={() => setPencil(r.user_id, false)}
                              sx={{ color: '#ef4444' }}>🚫 ✏️ off</Button>)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editW} onClose={() => setEditW(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e1e2e', color: '#fff' } }}>
        <DialogTitle sx={{ color: '#fff' }}>{`✏️ Редагувати слово #${editW?.wordId || ''}`}</DialogTitle>
        <DialogContent sx={INPUT_SX}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* D-242: фото слова + перегенерація прямо зі скарги */}
            {editW?.imageUrl && (
              <Box sx={{ textAlign: 'center' }}>
                <img src={editW.imageUrl} alt="" onClick={() => setZoomR(editW.imageUrl)}
                     style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, cursor: 'zoom-in' }} />
              </Box>
            )}
            <Stack direction="row" spacing={1} justifyContent="center">
              {histR.length > 0 && (
                <Button variant="outlined" size="small" disabled={genBusyR} onClick={backR}
                  sx={{ color: '#fbbf24', borderColor: '#b45309', minWidth: 42 }}>←</Button>
              )}
              <Button variant="outlined" size="small" disabled={genBusyR} onClick={genR}
                sx={{ color: '#34d399', borderColor: '#059669' }}>
                {genBusyR ? '⏳' : '🎲 Перегенерувати фото'}
              </Button>
              <Button component="label" variant="outlined" size="small" disabled={genBusyR}
                sx={{ color: 'var(--ac2, #a78bfa)', borderColor: '#7c3aed' }}>
                📷 Своє фото
                <input type="file" hidden accept="image/jpeg,image/png,image/webp"
                  onChange={e => { if (e.target.files[0]) uploadR(e.target.files[0]); e.target.value = ''; }} />
              </Button>
            </Stack>
            <TextField label="Слово (en)" value={editW?.en || ''} fullWidth
              onChange={e => setEditW(p => ({ ...p, en: e.target.value }))} />
            <TextField label="Переклад (uk)" value={editW?.uk || ''} fullWidth
              onChange={e => setEditW(p => ({ ...p, uk: e.target.value }))} />
            <Typography variant="caption" sx={{ color: '#888' }}>Інші мови перекладу зберігаються без змін.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditW(null)}>Скасувати</Button>
          <Button disabled={savingW || !editW?.en} onClick={() => saveW(false)}>{savingW ? '⏳' : '💾 Зберегти'}</Button>
          <Button variant="contained" color="success" disabled={savingW || !editW?.en} onClick={() => saveW(true)}>{savingW ? '⏳' : '✅ Виправлено'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!zoomR} onClose={() => setZoomR(null)} maxWidth="md"
        PaperProps={{ sx: { bgcolor: '#111', p: 1 } }}>
        {zoomR && <img src={zoomR} alt="" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }}
                       onClick={() => setZoomR(null)} />}
      </Dialog>
    </Stack>
  );
}

// =============================================================================
// 💎 PlayLanguage (Batch B #4): user custom words — sync + "Add to pool"
// =============================================================================
function CustomWordsTab() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy] = useState(false);
  // 💎 CRUD (2026-08-13): видалення/редагування власних слів юзерів + картинка.
  const [editW, setEditW] = useState(null);   // { wordId, translations:{}, imageUrl }
  const [savingW, setSavingW] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [histW, setHistW] = useState([]);     // історія фото (для ←)
  const [zoom, setZoom] = useState(null);

  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/words/custom?status=${filter}`)
      .then(r => setList(r.words || []))
      .catch(e => alert(e.message));
  }, [filter]);
  useEffect(load, [load]);

  const setPool = async (id, pooled) => {
    if (pooled && !window.confirm('Додати це слово у спільний пул? Його зможуть вивчати всі.')) return;
    if (!pooled && !window.confirm('Прибрати слово зі спільного пулу (зробити приватним)?')) return;
    setBusy(true);
    try {
      await jsonFetch(`${API_BASE}/words/${id}/pool`,
        { method: 'POST', body: JSON.stringify({ pooled }) });
      load();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const delWord = (w) => {
    const label = Object.values(w.translations || {})[0] || w.word_en || ('#' + w.id);
    if (!window.confirm(`ВИДАЛИТИ слово «${label}» НАЗАВЖДИ (з бази повністю)? Це незворотно.`)) return;
    setBusy(true);
    jsonFetch(`${API_BASE}/words/${w.id}`, { method: 'DELETE' })
      .then(load).catch(e => alert(e.message)).finally(() => setBusy(false));
  };
  // 💎 Редактор перекладів ГЕНЕРИЧНИЙ по мовах -- слово могло бути створене на
  // будь-якій мові вивчення (es, de, ...), не лише en/uk.
  const openEdit = (w) => {
    setHistW([]);
    setEditW({ wordId: w.id, translations: { ...(w.translations || {}) }, imageUrl: w.image_url || null });
  };
  const setTr = (lang, val) => setEditW(p => ({ ...p, translations: { ...p.translations, [lang]: val } }));
  const saveW = async () => {
    if (!editW) return;
    setSavingW(true);
    try {
      const body = { translations: editW.translations };
      if (editW.translations.en) body.word_en = editW.translations.en;
      await jsonFetch(`${API_BASE}/words/${editW.wordId}`, { method: 'PUT', body: JSON.stringify(body) });
      setEditW(null); load();
    } catch (e) { alert(e.message); } finally { setSavingW(false); }
  };
  const genInDialog = async () => {
    if (!editW || genBusy) return;
    setGenBusy(true);
    const prev = editW.imageUrl || null;
    try {
      const d = await jsonFetch(`${API_BASE}/words/${editW.wordId}/generate-image`, { method: 'POST', body: '{}' });
      if (prev) setHistW(h => [...h, prev]);
      setEditW(p => ({ ...p, imageUrl: d.image_url }));
      load();
    } catch (e) { alert(e.message); } finally { setGenBusy(false); }
  };
  const backInDialog = async () => {
    if (!editW || genBusy || !histW.length) return;
    const prev = histW[histW.length - 1];
    setGenBusy(true);
    try {
      await jsonFetch(`${API_BASE}/words/${editW.wordId}`, { method: 'PUT', body: JSON.stringify({ image_url: prev }) });
      setHistW(h => h.slice(0, -1));
      setEditW(p => ({ ...p, imageUrl: prev }));
      load();
    } catch (e) { alert(e.message); } finally { setGenBusy(false); }
  };
  const uploadInDialog = async (file) => {
    if (!editW || genBusy || !file) return;
    setGenBusy(true);
    const prev = editW.imageUrl || null;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/words/${editW.wordId}/upload-image`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
      const d = await res.json();
      if (prev) setHistW(h => [...h, prev]);
      setEditW(p => ({ ...p, imageUrl: d.image_url }));
      load();
    } catch (e) { alert(e.message); } finally { setGenBusy(false); }
  };

  return (
    <Stack spacing={3} sx={INPUT_SX}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ color: '#fff' }}>➕ Власні слова користувачів</Typography>
        <Box flex={1} />
        {['pending', 'pooled', 'all'].map(s =>
          <Chip key={s} label={s} onClick={() => setFilter(s)}
            variant={filter === s ? 'filled' : 'outlined'}
            sx={{ bgcolor: filter === s ? 'var(--ac1, #7c4dff)' : 'transparent',
                  color: filter === s ? '#fff' : '#aaa', borderColor: '#555' }} />)}
      </Stack>
      {list.length === 0 && <Alert severity="info">Немає слів зі статусом "{filter}"</Alert>}
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['ID','Час','Автор','Картинка','Слово (translations)','Пул','Actions'].map(c =>
                <TableCell key={c} sx={{ color: '#aaa' }}>{c}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(w => (
              <TableRow key={w.id} sx={{ '& td': { color: '#fff', verticalAlign: 'top' } }}>
                <TableCell>#{w.id}</TableCell>
                <TableCell>{fmtDate(w.created_at)}</TableCell>
                <TableCell>
                  {w.author_username ? `@${w.author_username}` : '—'}
                  <br />
                  <span style={{ color: '#888', fontSize: '0.75rem' }}>
                    tg:{w.author_telegram_id} db:{w.created_by_user_id || '?'}
                  </span>
                </TableCell>
                <TableCell>
                  {w.image_url
                    ? <img src={w.image_url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                    : <span style={{ color: '#666' }}>—</span>}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ color: 'var(--ac2, #a78bfa)', whiteSpace: 'pre-wrap', display: 'block', maxWidth: 320 }}>
                    {Object.entries(w.translations || {}).map(([k, v]) => `${k}: ${v}`).join('\n') || w.word_en}
                  </Typography>
                  {w.category &&
                    <span style={{ color: '#666', fontSize: '0.7rem' }}>{w.category} · L{w.difficulty}</span>}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={w.pooled ? 'у пулі' : 'приватне'}
                    color={w.pooled ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {w.pooled
                    ? <Button size="small" disabled={busy} onClick={() => setPool(w.id, false)}
                              sx={{ color: '#ef4444' }}>↩ Прибрати з пулу</Button>
                    : <Button size="small" disabled={busy} onClick={() => setPool(w.id, true)}
                              sx={{ color: '#5eead4' }}>➕ Додати в пул</Button>}
                  <Tooltip title="Редагувати слово/картинку"><span>
                    <Button size="small" disabled={busy} onClick={() => openEdit(w)} sx={{ color: 'var(--ac2, #a78bfa)', minWidth: 36 }}>✏️</Button>
                  </span></Tooltip>
                  <Tooltip title="Видалити назавжди"><span>
                    <Button size="small" disabled={busy} onClick={() => delWord(w)} sx={{ color: '#ef4444', minWidth: 36 }}>🗑</Button>
                  </span></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={!!editW} onClose={() => setEditW(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e1e2e', color: '#fff' } }}>
        <DialogTitle sx={{ color: '#fff' }}>{`✏️ Редагувати слово #${editW?.wordId || ''}`}</DialogTitle>
        <DialogContent sx={INPUT_SX}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editW?.imageUrl && (
              <Box sx={{ textAlign: 'center' }}>
                <img src={editW.imageUrl} alt="" onClick={() => setZoom(editW.imageUrl)}
                     style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, cursor: 'zoom-in' }} />
              </Box>
            )}
            <Stack direction="row" spacing={1} justifyContent="center">
              {histW.length > 0 && (
                <Button variant="outlined" size="small" disabled={genBusy} onClick={backInDialog}
                  sx={{ color: '#fbbf24', borderColor: '#b45309', minWidth: 42 }}>←</Button>
              )}
              <Button variant="outlined" size="small" disabled={genBusy} onClick={genInDialog}
                sx={{ color: '#34d399', borderColor: '#059669' }}>
                {genBusy ? '⏳' : '🎲 Перегенерувати фото'}
              </Button>
              <Button component="label" variant="outlined" size="small" disabled={genBusy}
                sx={{ color: 'var(--ac2, #a78bfa)', borderColor: '#7c3aed' }}>
                📷 Своє фото
                <input type="file" hidden accept="image/jpeg,image/png,image/webp"
                  onChange={e => { if (e.target.files[0]) uploadInDialog(e.target.files[0]); e.target.value = ''; }} />
              </Button>
            </Stack>
            {Object.entries(editW?.translations || {}).map(([lang, val]) => (
              <TextField key={lang} label={`${WORD_LANG_FLAGS[lang] || ''} ${lang}`} value={val || ''} fullWidth
                onChange={e => setTr(lang, e.target.value)} />
            ))}
            <Typography variant="caption" sx={{ color: '#888' }}>
              Показані всі мови, збережені для цього слова. Порожнє поле = переклад відсутній.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditW(null)}>Скасувати</Button>
          <Button variant="contained" disabled={savingW} onClick={saveW} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Зберегти</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="md"
        PaperProps={{ sx: { bgcolor: '#111', p: 1 } }}>
        {zoom && <img src={zoom} alt="" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }}
                      onClick={() => setZoom(null)} />}
      </Dialog>
    </Stack>
  );
}

// 💎 D-240: «Асоціації» — всі слова, де юзери тиснули «Фото не асоціюється» (диспути) + дії.
function PlAssociationsTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editW, setEditW] = useState(null);
  const [savingW, setSavingW] = useState(false);
  const [zoom, setZoom] = useState(null);     // лайтбокс фото
  const [genBusy, setGenBusy] = useState(false);
  const [histW, setHistW] = useState([]);     // ← історія фото в діалозі
  const load = useCallback(() => {
    jsonFetch(`${API_BASE}/associations`)
      .then(r => setItems(r.items || []))
      .catch(e => alert(e.message));
  }, []);
  useEffect(load, [load]);
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); load(); } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const genImage = (w) => act(() =>
    jsonFetch(`${API_BASE}/words/${w.id}/generate-image`, { method: 'POST', body: '{}' }));
  const poolRefresh = (w) => {
    if (!window.confirm(`Пересіяти пул фото для «${w.word_en}»? Голоси гри скинуться, слово повернеться в гру.`)) return;
    act(() => jsonFetch(`${API_BASE}/words/${w.id}/photo-pool-refresh`, { method: 'POST', body: '{}' }));
  };
  const clearDisputes = (w) => {
    if (!window.confirm(`Зняти «${w.word_en}» з гри? (диспути очистяться, слово зникне з цього списку)`)) return;
    act(() => jsonFetch(`${API_BASE}/words/${w.id}/disputes-clear`, { method: 'POST', body: '{}' }));
  };
  const delWord = (w) => {
    if (!window.confirm(`ВИДАЛИТИ слово «${w.word_en}» повністю? Це незворотно.`)) return;
    act(() => jsonFetch(`${API_BASE}/words/${w.id}`, { method: 'DELETE' }));
  };
  const openEdit = async (w) => {
    try {
      const res = await jsonFetch(`${API_BASE}/words?word_id=${w.id}&limit=1`);
      const full = (res.items || [])[0];
      if (!full) { alert('Слово не знайдено'); return; }
      const tr = full.translations || {};
      setHistW([]);
      setEditW({ wordId: full.id, translations: tr, en: full.word_en || tr.en || '',
                 uk: tr.uk || full.word_ua || '', imageUrl: full.image_url || null });
    } catch (e) { alert(e.message); }
  };
  const saveW = async () => {
    if (!editW) return;
    setSavingW(true);
    try {
      const translations = { ...editW.translations, en: editW.en, uk: editW.uk };
      await jsonFetch(`${API_BASE}/words/${editW.wordId}`,
        { method: 'PUT', body: JSON.stringify({ word_en: editW.en, translations }) });
      setEditW(null); load();
    } catch (e) { alert(e.message); } finally { setSavingW(false); }
  };
  // 🎲 перегенерувати фото прямо з діалогу редагування (+ ← повернення)
  const genInDialog = async () => {
    if (!editW || genBusy) return;
    setGenBusy(true);
    const prev = editW.imageUrl || null;
    try {
      const d = await jsonFetch(`${API_BASE}/words/${editW.wordId}/generate-image`, { method: 'POST', body: '{}' });
      if (prev) setHistW(h => [...h, prev]);
      setEditW(p => ({ ...p, imageUrl: d.image_url }));
      load();
    } catch (e) { alert(e.message); } finally { setGenBusy(false); }
  };
  const backInDialog = async () => {
    if (!editW || genBusy || !histW.length) return;
    const prev = histW[histW.length - 1];
    setGenBusy(true);
    try {
      await jsonFetch(`${API_BASE}/words/${editW.wordId}`,
        { method: 'PUT', body: JSON.stringify({ image_url: prev }) });
      setHistW(h => h.slice(0, -1));
      setEditW(p => ({ ...p, imageUrl: prev }));
      load();
    } catch (e) { alert(e.message); } finally { setGenBusy(false); }
  };
  // 📷 D-243: своє фото, коли Pexels нічого підходящого не дає
  const uploadInDialog = async (file) => {
    if (!editW || genBusy || !file) return;
    setGenBusy(true);
    const prev = editW.imageUrl || null;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/words/${editW.wordId}/upload-image`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem('dashboard_token') || ''}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
      const d = await res.json();
      if (prev) setHistW(h => [...h, prev]);
      setEditW(p => ({ ...p, imageUrl: d.image_url }));
      load();
    } catch (e) { alert(e.message); } finally { setGenBusy(false); }
  };
  if (items === null) return <CircularProgress />;
  return (
    <Stack spacing={2} sx={INPUT_SX}>
      <Typography variant="h6" sx={{ color: '#fff' }}>🧩 Асоціації — позначені фото</Typography>
      <Typography variant="caption" sx={{ color: '#888' }}>
        Всі слова, де юзери тиснули «Фото не асоціюється». Дії: ✏️ редагувати · 🎲 нове головне фото ·
        🔄 пересіяти пул гри (скидає голоси) · 🧹 зняти з гри · 🗑 видалити слово.
      </Typography>
      {items.length === 0 && <Alert severity="info">Поки порожньо — ніхто не позначав фото</Alert>}
      {items.length > 0 && (
        <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Фото', 'Слово', '🗣', '🗳', '🚫', 'Останнє', 'Статус', 'Дії'].map(h =>
                <TableCell key={h} sx={{ color: '#aaa', fontWeight: 'bold' }}>{h}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {items.map(w => (
                <TableRow key={w.id} sx={{ '& td': { verticalAlign: 'top' } }}>
                  <TableCell>
                    {w.image_url
                      ? <img src={w.image_url} alt="" onClick={() => setZoom(w.image_url)}
                             style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }} />
                      : <span style={{ color: '#666' }}>—</span>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#fff' }}>{w.word_en}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--ac2, #a78bfa)' }}>{w.word_uk || ''}</Typography>
                    <br /><span style={{ color: '#666', fontSize: '0.7rem' }}>#{w.id} · {w.category || '—'} · пул {w.pool_size}</span>
                  </TableCell>
                  <TableCell><Chip size="small" label={w.disputers} sx={{ bgcolor: 'rgba(251,146,60,0.2)', color: '#fdba74' }} /></TableCell>
                  <TableCell><Typography variant="caption" sx={{ color: '#5eead4' }}>{w.votes}</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ color: w.none_votes ? '#fca5a5' : '#666' }}>{w.none_votes}</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ color: '#888' }}>{fmtDate(w.last_dispute)}</Typography></TableCell>
                  <TableCell>
                    {w.locked
                      ? <Chip size="small" label="🔒" color="success" />
                      : (w.enabled
                          ? <Chip size="small" label="у грі" sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.25)', color: '#c4b5fd' }} />
                          : <Chip size="small" label="off" />)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Редагувати слово"><span>
                      <Button size="small" disabled={busy} onClick={() => openEdit(w)} sx={{ color: 'var(--ac2, #a78bfa)', minWidth: 36 }}>✏️</Button>
                    </span></Tooltip>
                    <Tooltip title="Нове головне фото"><span>
                      <Button size="small" disabled={busy} onClick={() => genImage(w)} sx={{ color: '#34d399', minWidth: 36 }}>🎲</Button>
                    </span></Tooltip>
                    <Tooltip title="Пересіяти пул гри"><span>
                      <Button size="small" disabled={busy} onClick={() => poolRefresh(w)} sx={{ color: '#5eead4', minWidth: 36 }}>🔄</Button>
                    </span></Tooltip>
                    <Tooltip title="Зняти з гри"><span>
                      <Button size="small" disabled={busy} onClick={() => clearDisputes(w)} sx={{ color: '#fbbf24', minWidth: 36 }}>🧹</Button>
                    </span></Tooltip>
                    <Tooltip title="Видалити слово"><span>
                      <Button size="small" disabled={busy} onClick={() => delWord(w)} sx={{ color: '#ef4444', minWidth: 36 }}>🗑</Button>
                    </span></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!editW} onClose={() => setEditW(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#1e1e2e', color: '#fff' } }}>
        <DialogTitle sx={{ color: '#fff' }}>{`✏️ Редагувати слово #${editW?.wordId || ''}`}</DialogTitle>
        <DialogContent sx={INPUT_SX}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editW?.imageUrl && (
              <Box sx={{ textAlign: 'center' }}>
                <img src={editW.imageUrl} alt="" onClick={() => setZoom(editW.imageUrl)}
                     style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, cursor: 'zoom-in' }} />
              </Box>
            )}
            <Stack direction="row" spacing={1} justifyContent="center">
              {histW.length > 0 && (
                <Button variant="outlined" size="small" disabled={genBusy} onClick={backInDialog}
                  sx={{ color: '#fbbf24', borderColor: '#b45309', minWidth: 42 }}>←</Button>
              )}
              <Button variant="outlined" size="small" disabled={genBusy} onClick={genInDialog}
                sx={{ color: '#34d399', borderColor: '#059669' }}>
                {genBusy ? '⏳' : '🎲 Перегенерувати фото'}
              </Button>
              <Button component="label" variant="outlined" size="small" disabled={genBusy}
                sx={{ color: 'var(--ac2, #a78bfa)', borderColor: '#7c3aed' }}>
                📷 Своє фото
                <input type="file" hidden accept="image/jpeg,image/png,image/webp"
                  onChange={e => { if (e.target.files[0]) uploadInDialog(e.target.files[0]); e.target.value = ''; }} />
              </Button>
            </Stack>
            <TextField label="Слово (en)" value={editW?.en || ''} fullWidth
              onChange={e => setEditW(p => ({ ...p, en: e.target.value }))} />
            <TextField label="Переклад (uk)" value={editW?.uk || ''} fullWidth
              onChange={e => setEditW(p => ({ ...p, uk: e.target.value }))} />
            <Typography variant="caption" sx={{ color: '#888' }}>Інші мови перекладу зберігаються без змін.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditW(null)}>Скасувати</Button>
          <Button variant="contained" disabled={savingW} onClick={saveW} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>Зберегти</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="md"
        PaperProps={{ sx: { bgcolor: '#111', p: 1 } }}>
        {zoom && <img src={zoom} alt="" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }}
                      onClick={() => setZoom(null)} />}
      </Dialog>
    </Stack>
  );
}

// 💎 PlayLanguage — реферальна атрибуція: хто кого привів + УСІ PL-юзери з джерелом (органічні).
function PlReferralsTab({ onCountChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flt, setFlt] = useState('all'); // all | referred | organic
  // 💎 2026-09-11: 🆕-позначка нових — своя мітка «прочитано» саме цієї вкладки
  const [newKeys, setNewKeys] = useState(null);
  useEffect(() => {
    jsonFetch(`${API_BASE}/referrals`)
      .then(r => {
        setData(r); setLoading(false);
        setNewKeys(new Set((r.referrals || []).filter(x => x.is_new).map(x => String(x.referred_tg))));
        jsonFetch(`${API_BASE}/new-users/mark-seen`, { method: 'POST', body: JSON.stringify({ tab: 'referrals' }) })
          .then(() => onCountChange && onCountChange()).catch(() => {});
      })
      .catch(e => { alert(e.message); setLoading(false); });
  }, [onCountChange]);
  if (loading) return <CircularProgress />;
  const top = (data && data.top_referrers) || [];
  const allRefs = (data && data.referrals) || [];
  const sm = (data && data.summary) || { pl_users: allRefs.length, with_referrer: 0, organic: 0 };
  const refs = allRefs.filter(r => flt === 'all' ? true : flt === 'organic' ? !r.has_referrer : r.has_referrer);
  const fltChip = (key, label) => (
    <Chip size="small" label={label} onClick={() => setFlt(key)}
          sx={{ cursor: 'pointer', bgcolor: flt === key ? 'rgba(var(--ac1rgb, 124, 77, 255),0.45)' : 'rgba(255,255,255,0.06)',
                color: flt === key ? '#fff' : '#aaa' }} />
  );
  return (
    <Stack spacing={2} sx={INPUT_SX}>
      <Typography variant="h6" sx={{ color: '#fff' }}>🔗 Хто кого привів у PlayLanguage</Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" label={`👥 PL-юзерів: ${sm.pl_users}`} sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.2)', color: '#c4b5fd' }} />
        <Chip size="small" label={`🔗 з реферером: ${sm.with_referrer}`} sx={{ bgcolor: 'rgba(94,234,212,0.15)', color: '#5eead4' }} />
        <Chip size="small" label={`🌱 органічних: ${sm.organic}`} sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#bbb' }} />
        {newKeys && newKeys.size > 0 && (
          <Chip size="small" label={`🆕 нових: ${newKeys.size}`}
            sx={{ bgcolor: 'rgba(255,45,45,0.25)', color: '#ff8a80', fontWeight: 700 }} />
        )}
      </Stack>
      <Typography variant="caption" sx={{ color: '#777' }}>
        ⓘ Реферер фіксується для НОВОГО юзера через бот-лінк <code>?start=ref_…</code> 🤖 або
        апка-лінк <code>?startapp=ref_…</code> 📱. Хто прийшов без реф-лінка → 🌱 органічний.
      </Typography>

      <Typography variant="subtitle2" sx={{ color: 'var(--ac2, #a78bfa)' }}>🏆 Топ-реферери ({top.length})</Typography>
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead><TableRow>
            {['Реферер', 'Привів у PL', 'З них активні'].map(h =>
              <TableCell key={h} sx={{ color: '#aaa', fontWeight: 'bold' }}>{h}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {top.length === 0 && (
              <TableRow><TableCell colSpan={3}><Typography variant="caption" sx={{ color: '#888' }}>Поки немає рефералів</Typography></TableCell></TableRow>
            )}
            {top.map((t, i) => (
              <TableRow key={i}>
                <TableCell><Typography variant="body2" sx={{ color: '#fff' }}>{t.referrer ? '@' + t.referrer : 'id:' + t.referrer_id}</Typography></TableCell>
                <TableCell><Chip size="small" label={t.invited} sx={{ bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.25)', color: '#c4b5fd' }} /></TableCell>
                <TableCell><Typography variant="caption" sx={{ color: '#5eead4' }}>{t.invited_active}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'var(--ac2, #a78bfa)', mr: 1 }}>📋 Усі PL-юзери та джерело</Typography>
        {fltChip('all', `всі (${allRefs.length})`)}
        {fltChip('referred', `з реферером (${sm.with_referrer})`)}
        {fltChip('organic', `органічні (${sm.organic})`)}
      </Stack>
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
        <Table size="small">
          <TableHead><TableRow>
            {['Юзер', '← Джерело', 'Коли', 'Мова', 'Слів', 'Статус'].map(h =>
              <TableCell key={h} sx={{ color: '#aaa', fontWeight: 'bold' }}>{h}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {refs.length === 0 && (
              <TableRow><TableCell colSpan={6}><Typography variant="caption" sx={{ color: '#888' }}>Немає даних</Typography></TableCell></TableRow>
            )}
            {refs.map((r, i) => (
              <TableRow key={i}
                sx={newKeys && newKeys.has(String(r.referred_tg)) ? { bgcolor: 'rgba(255,45,45,0.13)' } : undefined}>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#fff' }}>
                    {r.referred ? '@' + r.referred : 'tg:' + r.referred_tg}
                    {newKeys && newKeys.has(String(r.referred_tg)) && (
                      <Box component="span" sx={{ ml: 0.5, fontSize: 11, fontWeight: 700, color: '#ff8a80' }}>🆕</Box>
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  {r.has_referrer
                    ? <Typography variant="caption" sx={{ color: '#c4b5fd' }}>
                        {r.referrer ? '@' + r.referrer : 'id:' + r.referrer_id}
                        {r.source === 'miniapp_startparam' && <span title="через апку (?startapp)" style={{ marginLeft: 4 }}>📱</span>}
                        {r.source === 'bot_start' && <span title="через бот (/start)" style={{ marginLeft: 4 }}>🤖</span>}
                      </Typography>
                    : <Typography variant="caption" sx={{ color: '#777', fontStyle: 'italic' }}>🌱 органічний</Typography>}
                </TableCell>
                <TableCell><Typography variant="caption" sx={{ color: '#888' }}>{fmtDate(r.created_at)}</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ color: '#bbb' }}>{r.learn_lang || '—'}</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ color: '#fff' }}>{r.words_learned}</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ color: '#bbb' }}>{r.status || '—'}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

// 💎 D-285: вкладка тех-підтримки PL — список звернень + переписка + відповідь.
function SupportTab() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const loadList = useCallback(() => {
    jsonFetch(`${API_BASE}/support/list`).then(setList).catch(e => setErr(String(e)));
  }, []);
  useEffect(() => { loadList(); const id = setInterval(loadList, 20000); return () => clearInterval(id); }, [loadList]);

  const openThread = (uid) => {
    setSel(uid); setMsgs([]); setErr(null);
    jsonFetch(`${API_BASE}/support/${uid}`).then(rows => { setMsgs(rows); loadList(); }).catch(e => setErr(String(e)));
  };

  const sendReply = async () => {
    const t = reply.trim();
    if (!t || !sel || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await jsonFetch(`${API_BASE}/support/${sel}/reply`, { method: 'POST', body: JSON.stringify({ message: t }) });
      setReply('');
      if (r && r.sent === false) setErr('⚠️ Збережено, але не доставлено (юзер міг не запускати @PlayLanguage_bot).');
      jsonFetch(`${API_BASE}/support/${sel}`).then(setMsgs);
      loadList();
    } catch (e) { setErr(String(e)); }
    setBusy(false);
  };

  const delMessage = async (id) => {
    try {
      await jsonFetch(`${API_BASE}/support/message/${id}`, { method: 'DELETE' });
      setMsgs(prev => prev.filter(m => m.id !== id));
      loadList();
    } catch (e) { setErr(String(e)); }
  };

  const delConversation = async () => {
    if (!sel || !window.confirm('Видалити всю переписку з цим юзером?')) return;
    try {
      await jsonFetch(`${API_BASE}/support/conversation/${sel}`, { method: 'DELETE' });
      setSel(null); setMsgs([]); loadList();
    } catch (e) { setErr(String(e)); }
  };

  // 💎 D-285d: перемикач — показувати юзеру inline-кнопку «Відповісти» під відповіддю чи ні.
  const [replyBtn, setReplyBtn] = useState(true);
  useEffect(() => {
    jsonFetch(`${API_BASE}/config`).then(c => {
      const v = c.pl_support_reply_btn;
      setReplyBtn(v === undefined || v === null || (v !== false && String(v) !== 'false'));
    }).catch(() => {});
  }, []);
  const toggleReplyBtn = async () => {
    const nv = !replyBtn;
    setReplyBtn(nv);
    try {
      await jsonFetch(`${API_BASE}/config`, { method: 'PUT', body: JSON.stringify({ config: { pl_support_reply_btn: nv } }) });
    } catch (e) { setErr(String(e)); setReplyBtn(!nv); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>🛠 Тех-підтримка PL</Typography>
      <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
        Звернення з міні-аппа. Пишеш українською → авто-переклад на мову юзера → DM у @PlayLanguage_bot. Куди пересилати звернення — налаштуй у ⚙️ Config.
      </Typography>
      <FormControlLabel sx={{ color: '#fff', mb: 1 }}
        control={<Switch checked={replyBtn} onChange={toggleReplyBtn} />}
        label={<Typography sx={{ color: '#ddd', fontSize: 14 }}>Показувати юзеру кнопку «✉️ Відповісти» під відповіддю</Typography>} />
      {err && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Paper sx={{ bgcolor: 'rgba(0,0,0,0.3)', p: 1, flex: '0 0 280px', maxHeight: 480, overflow: 'auto' }}>
          {list.length === 0 && <Typography sx={{ color: '#777', p: 2 }}>Поки звернень немає.</Typography>}
          {list.map(c => (
            <Box key={c.user_id} onClick={() => openThread(c.user_id)}
              sx={{ p: 1, borderRadius: 1, cursor: 'pointer', mb: 0.5,
                bgcolor: sel === c.user_id ? 'rgba(var(--ac1rgb, 124, 77, 255),0.25)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.15)' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                  @{c.telegram_username || c.telegram_id || c.user_id}
                </Typography>
                {c.unread > 0 && <Chip size="small" label={c.unread} color="error" />}
              </Box>
              <Typography sx={{ color: '#999', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.last_dir === 'from_admin' ? '↩️ ' : ''}{c.last_msg}
              </Typography>
              <Typography sx={{ color: '#666', fontSize: 11 }}>{fmtDate(c.last_at)}</Typography>
            </Box>
          ))}
        </Paper>
        <Paper sx={{ bgcolor: 'rgba(0,0,0,0.3)', p: 2, flex: 1, minWidth: 300 }}>
          {!sel && <Typography sx={{ color: '#777' }}>← Обери звернення зі списку.</Typography>}
          {sel && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button size="small" color="error" variant="outlined" onClick={delConversation}>
                  🗑 Видалити розмову
                </Button>
              </Box>
              <Box sx={{ maxHeight: 380, overflow: 'auto', mb: 2 }}>
                {msgs.map(m => (
                  <Box key={m.id} sx={{ mb: 1, textAlign: m.direction === 'from_admin' ? 'right' : 'left' }}>
                    <Box sx={{ display: 'inline-block', maxWidth: '85%', p: 1, borderRadius: 2,
                      bgcolor: m.direction === 'from_admin' ? 'rgba(var(--ac1rgb, 124, 77, 255),0.3)' : 'rgba(255,255,255,0.08)' }}>
                      <Typography sx={{ color: '#fff', fontSize: 14, whiteSpace: 'pre-wrap', textAlign: 'left' }}>{m.message_uk || m.message}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                        <Typography sx={{ color: '#888', fontSize: 10 }}>{fmtDate(m.created_at)}</Typography>
                        <Box component="span" onClick={() => delMessage(m.id)} title="Видалити повідомлення"
                          sx={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#f44336',
                                px: 0.5, '&:hover': { color: '#ff7961' } }}>🗑 видалити</Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
                {msgs.length === 0 && <Typography sx={{ color: '#777' }}>Завантаження…</Typography>}
              </Box>
              <Stack direction="row" spacing={1}>
                <TextField fullWidth multiline maxRows={4} size="small" placeholder="Відповідь українською…"
                  value={reply} onChange={e => setReply(e.target.value)} sx={INPUT_SX}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendReply(); }} />
                <Button variant="contained" disabled={busy || !reply.trim()} onClick={sendReply}>
                  {busy ? '…' : '📩'}
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default function PlayLanguagePage() {
  const [tab, setTab] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [cardUnseen, setCardUnseen] = useState(0); // D-252: непереглянуті авто-оплати карткою
  const [securityCount, setSecurityCount] = useState(0);
  const [reportsCount, setReportsCount] = useState(0);
  const [supportPending, setSupportPending] = useState(0); // 💎 D-285d: розмови без відповіді
  // 💎 2026-09-11: нові юзери як непрочитане. Два лічильники, бо мітка «прочитано»
  // своя у кожної вкладки: подивився список юзерів — «Хто привів» ще світиться.
  const [newUsers, setNewUsers] = useState(0);
  const [newRefs, setNewRefs] = useState(0);

  // 💎 Drag&drop порядок вкладок (зберігається у localStorage цього браузера).
  const [tabOrder, setTabOrder] = useState(loadPlTabOrder);
  const _dragTab = useRef(null);
  const reorderTabs = useCallback((src, target) => {
    if (src == null || src === target) return;
    setTabOrder(prev => {
      const arr = prev.filter(x => x !== src);
      const ti = arr.indexOf(target);
      arr.splice(ti < 0 ? arr.length : ti, 0, src);
      try { localStorage.setItem(PL_TAB_ORDER_KEY, JSON.stringify(arr)); } catch (_e) { /* ignore */ }
      return arr;
    });
  }, []);
  const resetTabOrder = useCallback(() => {
    setTabOrder(Array.from({ length: PL_TAB_COUNT }, (_, i) => i));
    try { localStorage.removeItem(PL_TAB_ORDER_KEY); } catch (_e) { /* ignore */ }
  }, []);

  const reloadCount = useCallback(() => {
    jsonFetch('/api/dashboard/notifications-counts')
      .then(r => {
        setPendingCount(r.playlanguage_donations || 0);
        setCardUnseen(r.playlanguage_card_unseen || 0);
        setSecurityCount(r.playlanguage_security || 0);
        setReportsCount(r.playlanguage_reports || 0);
        setNewUsers(r.playlanguage_new_users || 0);
        setNewRefs(r.playlanguage_new_referrals || 0);
      })
      .catch(() => {});
    // 💎 D-285f: непрочитані звернення (зникає коли відкрив розмову — is_read).
    jsonFetch(`${API_BASE}/support/list`)
      .then(rows => setSupportPending((rows || []).filter(c => Number(c.unread) > 0).length))
      .catch(() => {});
  }, []);
  useEffect(() => {
    reloadCount();
    const t = setInterval(reloadCount, 60000);
    return () => clearInterval(t);
  }, [reloadCount]);

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 6 }}>
      <Typography variant="h4" sx={{ color: 'var(--ac1, #7c4dff)', fontWeight: 'bold', mb: 1 }}>
        💎 PlayLanguage 🎮
      </Typography>
      <Typography variant="body2" sx={{ color: '#888', mb: 0.5 }}>
        Адміністрування додатку "Language на ізі"
      </Typography>
      <Typography variant="caption" sx={{ color: '#666', mb: 3, display: 'block' }}>
        ↔️ Перетягни вкладки мишею, щоб змінити їх порядок ·{' '}
        <Box component="span" onClick={resetTabOrder}
             sx={{ color: 'var(--ac1, #7c4dff)', cursor: 'pointer', textDecoration: 'underline' }}>
          скинути порядок
        </Box>
      </Typography>

      <Paper sx={{ bgcolor: 'rgba(26,26,46,0.6)', backdropFilter: 'blur(10px)' }}>
        {(() => {
          const _bsx = { '& .MuiBadge-badge': { right: -14, top: 4 } };
          const tabDefs = [
            { id: 0, label: '⚙️ Config' },
            { id: 1, label: (
                <Badge badgeContent={pendingCount} color="error" sx={_bsx}>
                  💰 Donations{cardUnseen > 0 ? <span style={{ marginLeft: 4, color: '#34d399', fontWeight: 700 }}>🆕{cardUnseen}</span> : null}
                </Badge>
              ) },
            { id: 2, label: '💵 Дохід' },
            { id: 3, label: '📊 Затрати' },
            { id: 4, label: '📚 Words Pool' },
            { id: 5, label: <Badge badgeContent={newUsers} color="error" sx={_bsx}>👥 Users</Badge> },
            { id: 6, label: '📈 Analytics' },
            { id: 7, label: <Badge badgeContent={securityCount} color="error" sx={_bsx}>🛡 Безпека</Badge> },
            { id: 8, label: <Badge badgeContent={reportsCount} color="error" sx={_bsx}>✏️ Скарги на слова</Badge> },
            { id: 9, label: '➕ Власні слова' },
            { id: 10, label: '💎 Реф-дохід' },
            { id: 11, label: <Badge badgeContent={newRefs} color="error" sx={_bsx}>🔗 Хто привів</Badge> },
            { id: 12, label: '🧩 Асоціації' },
            { id: 13, label: <Badge badgeContent={supportPending} color="error" sx={_bsx}>🛠 Підтримка</Badge> },
          ];
          const byId = id => (tabDefs.find(d => d.id === id) || { label: '?' });
          return (
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
                  variant="scrollable" scrollButtons="auto"
                  sx={{
                    borderBottom: '1px solid #333',
                    '& .MuiTab-root': { color: '#aaa', textTransform: 'none' },
                    '& .Mui-selected': { color: 'var(--ac1, #7c4dff) !important' },
                    '& .MuiTabs-indicator': { bgcolor: 'var(--ac1, #7c4dff)' },
                  }}>
              {tabOrder.map(id => (
                <Tab key={id} value={id} label={byId(id).label}
                     draggable
                     onDragStart={() => { _dragTab.current = id; }}
                     onDragOver={e => e.preventDefault()}
                     onDrop={e => { e.preventDefault(); reorderTabs(_dragTab.current, id); _dragTab.current = null; }}
                     onDragEnd={() => { _dragTab.current = null; }}
                     sx={{ cursor: 'grab' }} />
              ))}
            </Tabs>
          );
        })()}
        <Box sx={{ p: 3 }}>
          <TabPanel value={tab} index={0}><ConfigTab /></TabPanel>
          <TabPanel value={tab} index={1}><DonationsTab onCountChange={reloadCount} /></TabPanel>
          <TabPanel value={tab} index={2}><IncomeTab /></TabPanel>
          <TabPanel value={tab} index={3}><ExpensesTab /></TabPanel>
          <TabPanel value={tab} index={4}><WordsTab /></TabPanel>
          <TabPanel value={tab} index={5}><UsersTab onCountChange={reloadCount} /></TabPanel>
          <TabPanel value={tab} index={6}>{PLACEHOLDER('Аналітика — L.6')}</TabPanel>
          <TabPanel value={tab} index={7}><SecurityTab onCountChange={reloadCount} /></TabPanel>
          <TabPanel value={tab} index={8}><ReportsTab onCountChange={reloadCount} /></TabPanel>
          <TabPanel value={tab} index={9}><CustomWordsTab /></TabPanel>
          <TabPanel value={tab} index={10}><ReferralEarningsTab /></TabPanel>
          <TabPanel value={tab} index={11}><PlReferralsTab onCountChange={reloadCount} /></TabPanel>
          <TabPanel value={tab} index={12}><PlAssociationsTab /></TabPanel>
          <TabPanel value={tab} index={13}><SupportTab /></TabPanel>
        </Box>
      </Paper>
    </Container>
  );
}
